import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import OraclePanel from './OraclePanel';
import Leaderboard from './Leaderboard';
import InfoTooltip from './InfoTooltip';
import UserGuideModal, { GuideTab } from './UserGuideModal';
import GlobalTicker, { ProphecyAlert } from './GlobalTicker';
import GodModePanel from './GodModePanel';
import FeedbackModal from './FeedbackModal';
import HistoryPanel from './components/HistoryPanel';
import AuditLog from './components/AuditLog';
import SocialPulseTicker from './components/SocialPulseTicker';
import PledgeCardGrid from './components/PledgeCardGrid';
import DivergenceAlphaBox from './components/DivergenceAlphaBox';
import MintModal from './components/MintModal';
import EnrolmentModal from './components/EnrolmentModal';
import GhostClaimModal from './components/GhostClaimModal';
import MintQueue from './components/MintQueue';
import SystemBriefingSidebar from './components/SystemBriefingSidebar';
import MarketFeedRow from './components/MarketFeedRow';
import { fetchMarketStatusOverlay } from './lib/marketStatusOverlay';
import {
  FEED_SORT_OPTIONS,
  FEED_SORT_DEFAULT,
  partitionAndSort,
  type FeedSortKey,
} from './lib/feedSort';
import {
  computePenumbraCorners,
  type CornerOutcome,
} from './lib/penumbraCorners';
import { oracleClassLabel } from './lib/oracleClassLabels';
import TacticalMarketCard from './components/TacticalMarketCard';
import BinaryMarketsPanel from './components/BinaryMarketsPanel';
import MarketFocusModal from './components/MarketFocusModal';
import PenumbraChart, { PenumbraOutcome } from './components/PenumbraChart';
import { LENS_OPTIONS, RegionLens } from './lib/socialPulse';
import { averageDivergenceForLens } from './lib/mockMarkets';
import { APP_VERSION, BUILD_STAMP, BUILD_TAG, ARCHITECT, BUILD_SHA, BUILD_BRANCH, BUILD_TIME, BUILD_STAMP_HUMAN } from './constants';
import { ensureProfile, recordPledgeRemote, useProfileRealtime, useUserBalance, setSimulatedDP, SIMULATED_CITIZEN_DP, SlippageCapError } from './lib/ledger';
import { BALANCE_UNIT_LABEL, IS_GAME_MODE, IS_STRIPPED_DOWN } from './lib/mode';
import { SUPABASE_DIAG } from './lib/supabaseClient';
import { useIdentity, TIER_META, IdentityLevel, tradeFeeForTier, weightMultiplierForTier, setForcedTier, dispatchEnrolmentRefresh } from './lib/identity';
import { useCitizenPromotion } from './lib/sandboxPowerUp';
import { useAllMarketsOutcomeAggregates, useMarketHasOperatorSeed, useMarketOutcomeAggregates } from './lib/priceImpact';
// Session B Phase C (2026-05-13): the cockpit's pre-flight slippage
// preview now uses the parimutuel math from parimutuelPricing.ts
// (mirror of the revised PL/pgSQL math). The old linear-approximation
// slippagePct from priceImpact diverged from the RPC in discovery
// territory — gate-4 surfaced this as a +22.54% display vs NULL
// RPC slippage. The old slippagePct + SLIPPAGE_THRESHOLD imports are
// dropped; the priceImpactWarning that consumed them is removed.
// Inventory §6 / §9 item 20.
import {
  prePledgePrice,
  computeEffectivePrice,
  postPledgePrice,
  slippagePct as parimutuelSlippagePct,
  payoutUnits,
} from './lib/parimutuelPricing';
import {
  listMintedMarkets,
  subscribeMintActivity as subscribeMintedMarketActivity,
  subscribeVetoEvents as subscribeMintedVetoEvents,
  vouchForMarket,
  vouchesRequiredFor,
} from './lib/mintedMarkets';
import { useViewport } from './lib/useViewport';
import { useSigner } from '@thirdweb-dev/react';

// ── Iframe Isolation ──────────────────────────────────────────────────
//
// Civic Pass was sunset in July 2025 and has been uninstalled. The
// main bundle no longer references any Civic package. When a Level-0
// wallet citizen reaches the pledge CTA we render a plain HTML button
// ("⚱️ PREPARE IDENTITY SEAL"). On click we dynamically load
// CivicPortal (name retained — it's the popup opener, not Civic-
// specific), which opens `/verify-identity-bridge.html` in a popup.
// The bridge now queries EAS (base-sepolia.easscan.org/graphql) for a
// non-revoked attestation on the connected wallet; on a hit it posts
// CIVIC_SUCCESS (name retained for the existing listener contract)
// and CivicPortal promotes the citizen. Popup crashes still cannot
// reach the Agora's main thread.

type PortalState =
  | { phase: 'idle' }
  | { phase: 'summoning' }
  | { phase: 'failed'; message: string }
  | { phase: 'ready'; Component: React.ComponentType };

function IdentitySealButton(): React.JSX.Element {
  const [state, setState] = useState<PortalState>({ phase: 'idle' });

  const summon = useCallback(async () => {
    setState({ phase: 'summoning' });
    try {
      const mod = await import('./components/Identity/CivicPortal');
      setState({ phase: 'ready', Component: mod.default });
    } catch (err) {
      setState({ phase: 'failed', message: (err as Error).message || 'portal unreachable' });
    }
  }, []);

  if (state.phase === 'ready') {
    const { Component } = state;
    return <Component />;
  }

  if (state.phase === 'summoning') {
    return (
      <div style={{
        width:         '100%',
        marginTop:     4,
        padding:       '14px 18px',
        border:        '1px dashed #a16207',
        borderRadius:  8,
        background:    'rgba(161,98,7,0.12)',
        color:         '#fcd34d',
        fontFamily:    'monospace',
        fontSize:      11,
        letterSpacing: '0.18em',
        textAlign:     'center',
      }}>
        ⚱️ OPENING IDENTITY BRIDGE…
      </div>
    );
  }

  if (state.phase === 'failed') {
    return (
      <>
        <button
          onClick={summon}
          style={{
            width:          '100%',
            marginTop:      4,
            padding:        '14px 18px',
            border:         '2px solid #f87171',
            borderRadius:   8,
            background:     'linear-gradient(135deg, #7f1d1d 0%, #991b1b 60%, #b91c1c 100%)',
            color:          '#fee2e2',
            fontFamily:     "'Cormorant Garamond', 'Georgia', serif",
            fontSize:       18,
            fontWeight:     700,
            letterSpacing:  '0.12em',
            cursor:         'pointer',
          }}>
          ⚠ IDENTITY PORTAL FAILED · RETRY
        </button>
        <p style={{
          marginTop:     8,
          color:         '#fca5a5',
          fontSize:      10,
          lineHeight:    1.55,
          fontFamily:    'monospace',
          letterSpacing: '0.04em',
          wordBreak:     'break-word',
        }}>
          {state.message}
        </p>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes identity-seal-pulse {
          0%, 100% { box-shadow: 0 0 0 rgba(252,211,77,0.0),  0 0 14px rgba(252,211,77,0.35) inset; }
          50%      { box-shadow: 0 0 28px rgba(252,211,77,0.65), 0 0 22px rgba(252,211,77,0.45) inset; }
        }
      `}</style>
      <button
        onClick={summon}
        style={{
          width:          '100%',
          marginTop:      4,
          padding:        '14px 18px',
          border:         '2px solid #fde047',
          borderRadius:   8,
          background:     'linear-gradient(135deg, #b45309 0%, #d97706 45%, #fbbf24 100%)',
          color:          '#1a0f00',
          fontFamily:     "'Cormorant Garamond', 'Georgia', serif",
          fontSize:       20,
          fontWeight:     700,
          letterSpacing:  '0.12em',
          cursor:         'pointer',
          animation:      'identity-seal-pulse 2.4s ease-in-out infinite',
          textShadow:     '0 1px 0 rgba(253,224,71,0.5)',
        }}>
        ⚱️ PREPARE IDENTITY SEAL
      </button>
      <p style={{
        marginTop:     8,
        color:         '#fcd34d',
        fontSize:      11,
        lineHeight:    1.55,
        fontFamily:    'monospace',
        letterSpacing: '0.04em',
      }}>
        ⚱️ One-time liveness scan via Civic Pass. Unlocks Global P2P.
      </p>
    </>
  );
}

const API_BASE = process.env.REACT_APP_API_URL || 'https://norwich-hub.onrender.com';

interface MultiMarket {
  market_id: string;
  title:     string;
  status:    string;
  outcomes:  Array<{ id: string; label: string; q: number; vibe: number }>;
  b:         number;
  // Flask seed payload includes a creation timestamp on every market;
  // threaded here for the LOCAL feed's "Newest market" sort option
  // (2026-05-20 market-feed-sort brief).
  created_at?:      string | null;
  // Populated by the overlay (extended 2026-05-20). Drives the
  // "Nearest closure" default sort on the LOCAL feed. Not in the Flask
  // response — Supabase public.markets is the authoritative source.
  challenge_deadline?: string | null;
  // Populated by the I-065 overlay when Arena Supabase reports the
  // market is SETTLED within the 48h visibility window. Absent for
  // OPEN markets and (post-overlay-filter) SETTLED-too-old markets.
  settled_at?:      string | null;
  winning_outcome?: string | null;
  // I-068 Brief 2 — oracle binding via the extended overlay's
  // foreign-table select on oracle_sources!primary_oracle_id. Null
  // when primary_oracle_id IS NULL (system-audit and any future
  // internal-test rows) or when the Supabase overlay was unreachable
  // (fail-open contract — cockpit renders without oracle line).
  oracle_display_text?:       string | null;
  oracle_class?:              number | null;
  oracle_default_holdback?:   number | null;
  market_holdback_hours?:     number | null;   // per-market override; null = use registry default
}

interface PriceOutcome {
  id:             string;
  label:          string;
  price:          number;
  seed_vibe:      number;
  composite_vibe: number;
  q:              number;
  // Populated when price-impact aggregation is applied. Optional so the
  // raw server shape still matches the interface during fetch.
  base_price?:    number;
  units_pledged?: number;
  cost_pledged?:  number;
}

// Human-readable countdown for the Penumbra centre rest-state on the
// cockpit mounting (2026-05-20 brief Part A). Returns undefined when
// the deadline is missing so the chart's centre falls through cleanly
// (the component guards on truthy centerBottom).
function formatCloseCountdown(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  const deadlineMs = Date.parse(iso);
  if (!Number.isFinite(deadlineMs)) return undefined;
  const diffMs = deadlineMs - Date.now();
  if (diffMs <= 0) return 'CLOSED';
  const diffMin = diffMs / 60_000;
  if (diffMin < 60) return 'CLOSING SOON';
  const diffHr = diffMin / 60;
  if (diffHr < 24) {
    const n = Math.floor(diffHr);
    return `CLOSES IN ${n} ${n === 1 ? 'HOUR' : 'HOURS'}`;
  }
  const n = Math.floor(diffHr / 24);
  return `CLOSES IN ${n} ${n === 1 ? 'DAY' : 'DAYS'}`;
}

const PALETTE = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ec4899','#06b6d4','#84cc16','#f97316'];
const FIELD_COLOR = '#c084fc';                                    // matches MarketPie
const PARTY_COLORS: Record<string, string> = {
  labour:       '#dc2626',
  green:        '#16a34a',
  conservative: '#2563eb',
  lib_dem:      '#f59e0b',
  reform_uk:    '#06b6d4',
};
const THRESHOLD   = 0.05; // same as MarketPie — outcomes below this are "Others"

// ── Protocol Fee ────────────────────────────────────────────────────────
//
// Rate is tier-scaled (see tradeFeeForTier in lib/identity):
//   Level 0/1 → 1.0%
//   Level 2   → 0.2%  (reward for Full KYC)
//
// The fee surfaces on the Pledge button and in the quote summary for
// every route so the Protocol Cost is visible before the citizen
// clicks. It is recorded on every pledge (pledges.trade_fee) regardless
// of route, but only wallet-mode pledges actually settle on-chain.

function prophecyMultiplier(price: number): number {
  // Session B Phase C (2026-05-13): the underdog-boost "Prophecy
  // Multiplier" is held back on the Arena per DECISIONS.md
  // 2026-05-13 I-046. Defensive short-circuit returns 1 (no boost)
  // when stripped — neutralises any consumer (PROPHECY ALERT
  // styling, outcome-row color picker, GlobalTicker alerts) that
  // wasn't gated at the mount site.
  if (IS_STRIPPED_DOWN) return 1;
  return Math.min(1 + 1 / Math.max(price * 10, 0.01), 11);
}

function isProphecy(o: PriceOutcome): boolean {
  // Session B Phase C (2026-05-13): "Prophecy" flag tied to the
  // held-back vibe-vs-price divergence signal. Defensive short-
  // circuit returns false when stripped — no row gets prophecy
  // styling, no PROPHECY ALERT badge, even if the Flask API
  // pre-seeded composite_vibe > 0.65 on an outcome.
  if (IS_STRIPPED_DOWN) return false;
  return o.composite_vibe > 0.65 && o.price <= 0.20;
}

function isField(o: { id: string; label: string }): boolean {
  return /^field$/i.test(o.id) || /the\s*field/i.test(o.label);
}

function rowColor(o: PriceOutcome, idx: number): string {
  if (isField(o))         return FIELD_COLOR;
  if (PARTY_COLORS[o.id]) return PARTY_COLORS[o.id];
  if (isProphecy(o))      return '#fcd34d';
  return PALETTE[idx % PALETTE.length];
}

export default function MultiPage(): React.JSX.Element {
  // Identity is resolved by whichever provider wraps this page:
  //   Ghost Mode  (/ · /multi) — citizen_XXX from localStorage
  //   Wallet Mode (/global)    — 0x… from the connected MetaMask wallet
  // When in wallet mode with nothing connected, `id` is null and we
  // render a connect prompt instead of the trading surface.
  const identity    = useIdentity();
  const currentUser = identity.id ?? '';
  const identityPill = identity.pill;
  const balance = useUserBalance(currentUser);
  // Wallet signer — only used when identity.mode === 'wallet'. In Ghost
  // mode this is always undefined (no wallet connected) and the
  // Signature Gate is skipped, which keeps the Norwich Hub flow
  // frictionless.
  const signer = useSigner();
  // Pulses the balance pill for ~1s after a successful pledge so the
  // deduction feels confirmed rather than silently re-rendered.
  const [balanceGlow, setBalanceGlow] = useState(false);
  // Subscribe to realtime profile updates so any external balance
  // change (admin top-up, settlement payout, second session) lands in
  // the local ledger and the balance pill immediately.
  useProfileRealtime(currentUser);

  // Hydrate / create the profile row whenever the active citizen changes.
  // If no profile exists, Supabase inserts one with the 500 OC founding
  // stipend; the balance pill updates via useUserBalance above. Any
  // failure is surfaced via the loud red banner so Dromide can read the
  // exact Postgres / Supabase error code straight off the screen.
  const [profileError, setProfileError] = useState<string | null>(null);
  useEffect(() => {
    setProfileError(null);
    if (!currentUser) return; // wallet mode, not connected yet
    ensureProfile(currentUser).catch((err: unknown) => {
      const text = err instanceof Error ? err.message : String(err);
      console.error('[ensureProfile] failed', err);
      setProfileError(text);
    });
  }, [currentUser]);

  // Arena Citizen-by-default. The Mint Cockpit is gated at Tier ≥ CITIZEN
  // (lib/sandboxPowerUp), and the Ascent Ladder is load-bearing for Live
  // but pure friction for invitees who land in Arena and need to reach
  // the protocol's most distinctive feature. So in IS_GAME_MODE we
  // auto-promote on first session via the same two primitives the
  // Verify-Identity Power-Up uses (setForcedTier + setSimulatedDP) —
  // calling them directly avoids the 3-second progress UX that the Power-
  // Up runs as pedagogy.
  //
  // The auto-promote is one-shot, gated by a localStorage flag distinct
  // from setForcedTier's own storage key. A tester can call
  // window.agoracle.resetPowerUp() to drop back to OBSERVER and the
  // demotion sticks across re-renders, because we do not clear the flag
  // on reset.
  useEffect(() => {
    if (!IS_GAME_MODE) return;
    // Session B Phase C (2026-05-13): when IS_STRIPPED_DOWN, skip
    // the silent auto-promote to CITIZEN. Tier exposure is held
    // back per DECISIONS.md 2026-05-13 I-046; bumping users to a
    // tier they don't visually have is incoherent. Default OBSERVER
    // (level 1) is enough to pass the place_pledge identity_level
    // gate. Inventory §6 (computational dead code).
    if (IS_STRIPPED_DOWN) return;
    if (!currentUser) return;
    if (typeof window === 'undefined') return;
    const FLAG = 'agoracle_arena_auto_promoted_v1';
    if (window.localStorage.getItem(FLAG) === '1') return;
    setForcedTier(IdentityLevel.CITIZEN);
    setSimulatedDP(currentUser, SIMULATED_CITIZEN_DP);
    window.localStorage.setItem(FLAG, '1');
  }, [currentUser]);

  // Arena page-name surface. Brand badge in the Hazard Stripe already
  // says ⬡ AGORACLE ARENA; the document title is set here for tab
  // disambiguation per DECISIONS.md 2026-05-05.
  useEffect(() => {
    if (!IS_GAME_MODE) return;
    document.title = 'Markets · Agoracle Arena';
  }, []);

  const [markets,         setMarkets]         = useState<MultiMarket[]>([]);
  const [activeId,        setActiveId]        = useState('');
  const [rawPrices,       setRawPrices]       = useState<PriceOutcome[]>([]);
  // LOCAL-lens feed sort (2026-05-20 brief). In-memory only; resets to
  // the default on reload per Decision deferred-persistence in §6.
  const [feedSortKey,     setFeedSortKey]     = useState<FeedSortKey>(FEED_SORT_DEFAULT);

  // v2.2 Dual-State Terminal — which market the Focus Modal is open on.
  // Null == modal closed; the feed row below is purely informational.
  // When set, we also sync `activeId` so the existing prices/quote
  // pipeline targets the focused market.
  const [focusMarketId, setFocusMarketId] = useState<string | null>(null);

  // Intelligence Terminal lens (§5). Single source of truth for both
  // the Social Pulse ticker and the main pledge-card grid. LOCAL
  // routes to the live AMM feed; other lenses render the sandbox
  // MarketCard pool.
  const [lens, setLens] = useState<RegionLens>('LOCAL');

  // Mint modal visibility (§6). v1.8.1 Mint Isolation relocated the
  // entry control into the Hazard Stripe (public/index.html) so every
  // tier can test it in the sandbox. The HTML button dispatches the
  // `agoracle:open-mint` CustomEvent — we listen once on mount.
  //
  // v2.3 Minting Cockpit — the Cockpit is Citizen-gated. Sub-Citizen
  // tiers (Seeker / Observer) see a disabled Hazard Stripe CTA; even
  // if the event somehow fires, the MintModal renders its internal
  // TierGate panel rather than the Reality Blueprint form.
  const [showMint, setShowMint] = useState(false);
  useEffect(() => {
    // Per DECISIONS.md 2026-05-13 I-046: Mint Cockpit held back from
    // the user-facing surface. Skip the event listener entirely so
    // even a stray `agoracle:open-mint` dispatch (e.g. from a stale
    // Hazard Stripe DOM that pre-dates the cockpit's hide step) has
    // no effect.
    if (IS_STRIPPED_DOWN) return;
    const open = (): void => setShowMint(true);
    window.addEventListener('agoracle:open-mint', open);
    return () => window.removeEventListener('agoracle:open-mint', open);
  }, []);

  // v2.3 Minting Cockpit — reflect the Tier-2 gate on the Hazard Stripe
  // CTA. The button lives in public/index.html, so we reach it via the
  // DOM and flip `disabled` + title on every identity change. Citizens
  // and above see the glowing mint button; Observers / Seekers see a
  // muted, non-clickable affordance.
  //
  // Session B (2026-05-13): when IS_STRIPPED_DOWN, the Mint CTA is
  // hidden entirely per DECISIONS.md 2026-05-13 I-046 (Mint Cockpit
  // held back from the user-facing surface). The button is in
  // public/index.html (cached HTML) — we hide it via DOM rather than
  // editing the static markup so the unhide path is a single flag flip
  // when the feature eventually rolls out per I-046's evidence-based
  // rollout pattern.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const btn = document.getElementById('agoracle-mint-cta') as HTMLButtonElement | null;
    if (!btn) return;
    if (IS_STRIPPED_DOWN) {
      btn.style.display = 'none';
      return;
    }
    btn.style.display = '';  // un-hide if flag toggles at runtime
    const locked = identity.tier < IdentityLevel.CITIZEN;
    btn.disabled = locked;
    btn.title = locked
      ? 'Reputation (DP) threshold not met. Reach Level 2 to Mint.'
      : 'Mint a new market — 500 OC Creation Bond.';
  }, [identity.tier]);

  // System Briefing sidebar — slide-out technical dossier reachable
  // via the ⌬ toggle in the header. v2.1.0's auto-open-on-first-visit
  // behaviour was retired 2026-05-08: first impression should be the
  // cockpit, not the briefing. The seen-flag write on close is kept
  // as future-proofing in case auto-open is ever reintroduced.
  const [briefingOpen, setBriefingOpen] = useState<boolean>(false);

  // Persist the seen-flag the first time the user closes the briefing
  // so the welcome doesn't keep popping back on every reload.
  const closeBriefing = React.useCallback((): void => {
    setBriefingOpen(false);
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem('agoracle_briefing_seen', '1'); } catch {}
    }
  }, []);

  // v2.2 §2 Veto Purge — flips true for 150ms when any market is
  // VOIDED_BY_SOVEREIGN. The main surface picks this up to apply the
  // RGB-split glitch class. Card-level dissolve is handled in MintQueue
  // itself, which subscribes to the same stream.
  const [vetoGlitchOn, setVetoGlitchOn] = useState(false);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsub = subscribeMintedVetoEvents(() => {
      setVetoGlitchOn(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setVetoGlitchOn(false), 150);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, []);

  // v2.3.1 Minting Cockpit Activation — surface PENDING Minted markets
  // in the Global Feed. A freshly minted market carries creator-supplied
  // title + lens but no pledges yet; it renders as a greyed
  // "STABILIZING…" row with its vouch count until Citizens clear the
  // tiered threshold. Subscribes to the same mint-activity stream the
  // MintQueue uses so vouches/vetoes/lifecycle ticks repaint the rail.
  const [pendingMintedTick, setPendingMintedTick] = useState(0);
  useEffect(() => subscribeMintedMarketActivity(() => setPendingMintedTick(t => t + 1)), []);
  const pendingMintedRows = useMemo(() => {
    void pendingMintedTick;
    return listMintedMarkets()
      .filter(m => m.status === 'PENDING')
      .map(m => ({
        id:          m.id,
        title:       m.title,
        lens:        m.lens as string,
        vouchCount:  m.vouches.size,
        vouchTarget: vouchesRequiredFor(m.lens),
        creatorId:   m.creatorId,
        alreadyVouched: identity.id ? m.vouches.has(identity.id) : false,
      }));
  }, [pendingMintedTick, identity.id]);

  // v2.4 Mobile — Tactical Card [VOUCH] CTA handler. Preflight is the
  // same contract vouchForMarket enforces: citizen+, not the creator,
  // not already vouched. Backend subscribeMintActivity tick repaints
  // the pending rail automatically on success.
  const handleVouchPending = useCallback((marketId: string) => {
    if (!identity.id)                              return;
    if (identity.tier < IdentityLevel.CITIZEN)     return;
    vouchForMarket(marketId, identity.id, identity.tier);
  }, [identity.id, identity.tier]);

  // Live pledge aggregates from public.pledges for the active market,
  // grouped by outcome_name. Market-scoped (closes the I-006 collision
  // risk that the global `useOutcomeAggregates` hook still carries).
  // Drives the canonical pre-pledge price displayed in the outcome
  // table and the pledge form preview — both surfaces now compute
  // from the same aggregate source via the same parimutuel formula.
  const aggregates = useMarketOutcomeAggregates(activeId, 8000);

  // Market-scoped pledge aggregates for every market the feed renders,
  // double-keyed by `market_id` then `outcome_name`. Closes the I-006
  // label-collision hazard the bare-label global form carried — UEFA's
  // "The Field" stays separate from BoE's "Hold at 3.75%" stays
  // separate from the political market's "Yes" / "No". The feed must
  // never read the bare-label form (see the 2026-05-21
  // feed-i006-and-breakpoint brief): pool, the Biggest-pool sort,
  // favourite, and outsider all read this map via
  // `feedAggregates[m.market_id]?.[o.label]`.
  const feedAggregates = useAllMarketsOutcomeAggregates(30000);

  // Unseeded-market guard. `undefined` until the first probe lands;
  // `false` only when we've affirmatively observed zero operator-seed
  // rows for the active market. The guard refuses pledges in that
  // state — see the soft-fail banner below the pledge form and the
  // early-return inside `placeBet`.
  const hasOperatorSeed = useMarketHasOperatorSeed(activeId);
  const marketUnseeded  = hasOperatorSeed === false;

  // I-065 — pledge UI is greyed out when the active market is SETTLED.
  // Visual cue; the load-bearing guard is the place_pledge RPC's
  // `status != 'OPEN'` check (20260513000000_parimutuel_engine_port.sql:462).
  const activeMarket  = markets.find(m => m.market_id === activeId);
  const marketSettled = activeMarket?.status === 'SETTLED';
  const settledWinner = marketSettled ? (activeMarket?.winning_outcome ?? null) : null;

  // `priceData` is the canonical view consumed by every surface below
  // — outcome table, pie, quote, signal calcs. Per the 2026-05-14
  // display-reconciliation work, the displayed price is the canonical
  // parimutuel pre-pledge price (`p_i / T`) computed from Supabase
  // pledge aggregates, NOT Render's LMSR softmax + linear nudge. The
  // Render base price travels through as `base_price` for any consumer
  // that still wants the original LMSR signal, but the headline `price`
  // is the same number the pledge form preview shows — eliminating the
  // 30.2% vs 23.4% trust-surface split that I-052 surfaced. When the
  // pool is empty (T = 0) `prePledgePrice` returns `1 / n_outcomes`,
  // which matches Render's q = 0 uniform softmax, so the empty-pool
  // visual is preserved.
  const priceData = useMemo<PriceOutcome[]>(() => {
    const totalPool = rawPrices.reduce(
      (sum, o) => sum + (aggregates[o.label]?.cost_total ?? 0),
      0,
    );
    const nOutcomes = rawPrices.length || 1;
    return rawPrices.map(o => {
      const agg = aggregates[o.label] ?? { units_total: 0, cost_total: 0, pledge_count: 0 };
      const displayedPrice = prePledgePrice(agg.cost_total, totalPool, nOutcomes);
      return {
        ...o,
        base_price:    o.price,           // Render LMSR — retained for legacy consumers.
        price:         displayedPrice,    // Canonical parimutuel p_i / T.
        units_pledged: agg.units_total,
        cost_pledged:  agg.cost_total,
      };
    });
  }, [rawPrices, aggregates]);

  // ── Oracle Pulse: glow the outcome row whose price just moved ───────
  // Compares each render's priceData against the last seen value per
  // outcome id; any id whose impact-adjusted price shifted by more than
  // 0.01pp is added to a short-lived set that drives a CSS animation.
  const prevPricesRef = useRef<Record<string, number>>({});
  const [glowIds, setGlowIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const changed = new Set<string>();
    for (const o of priceData) {
      const prev = prevPricesRef.current[o.id];
      if (prev !== undefined && Math.abs(prev - o.price) > 0.0001) changed.add(o.id);
      prevPricesRef.current[o.id] = o.price;
    }
    if (changed.size === 0) return;
    setGlowIds(prev => {
      const next = new Set(prev);
      changed.forEach(id => next.add(id));
      return next;
    });
    const t = setTimeout(() => {
      setGlowIds(prev => {
        const next = new Set(prev);
        changed.forEach(id => next.delete(id));
        return next;
      });
    }, 900);
    return () => clearTimeout(t);
  }, [priceData]);
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [shares,          setShares]          = useState('10');
  const [quote,           setQuote]           = useState<{ cost: number; price_after: number } | null>(null);
  const [buying,          setBuying]          = useState(false);
  const [msg,             setMsg]             = useState<{ text: string; ok: boolean } | null>(null);
  // Session B (2026-05-13): slippage-cap rejection details from
  // place_pledge RPC (errcode 23514 + JSON DETAIL). When set, the form
  // shows the four-price snapshot + a "Pledge with override" button
  // per go-brief §3(a) shape (iii). Cleared when shares change or
  // pledge succeeds.
  const [slippageReject,  setSlippageReject]  = useState<import('./lib/ledger').SlippageCapDetails | null>(null);
  const [error,           setError]           = useState<string | null>(null);
  const [othersOpen,      setOthersOpen]      = useState(false);
  // Session B Phase C (2026-05-13): lastMultiplier state removed —
  // the "Prophecy Multiplier applied! ⚡" success-message line was
  // ceremonial / tier-multiplier surface held back per
  // DECISIONS.md 2026-05-13 I-046. Inventory §6 / §9 item 8.
  const [guideOpen,       setGuideOpen]       = useState(false);
  const [guideTab,        setGuideTab]        = useState<GuideTab>('guide');
  const [godOpen,         setGodOpen]         = useState(false);
  const [feedbackOpen,    setFeedbackOpen]    = useState(false);
  // Arena enrolment surface (brief 2026-05-14). The modal is mounted
  // here so it lives outside .cockpit-shell — the iPhone universal
  // selector at .cockpit-main * carpets every descendant with
  // position:relative !important, which silently flattens overlays
  // rendered inside <main>. Same discipline as SystemBriefingSidebar.
  const [enrolmentOpen,   setEnrolmentOpen]   = useState(false);
  // After successful enrolment we hand the local Ghost id off to
  // GhostClaimModal so the user can fold their pre-enrolment pledges
  // into the new enrolled identity. null = no claim modal mounted.
  const [pendingGhostClaim, setPendingGhostClaim] =
    useState<{ ghostId: string; enrolledId: string } | null>(null);
  const [view,            setView]            = useState<'markets' | 'history'>('markets');
  const [drawerOpen,      setDrawerOpen]      = useState(false);
  const { isMobile, isNarrow }                = useViewport();
  const [verifyOverlay,   setVerifyOverlay]   = useState(false);

  // Post-magic-link auto-open. When the cockpit re-mounts after the
  // magic-link round-trip, identity.enrolment.status flips to
  // 'pending_enrolment' — open EnrolmentModal at Step 2 so the user
  // can pick a username and accept the T&Cs without an extra click.
  // Arena-only — Live's identity layer is Civic Pass + KYC.
  useEffect(() => {
    if (!IS_STRIPPED_DOWN) return;
    if (identity.enrolment?.status === 'pending_enrolment') {
      setEnrolmentOpen(true);
    }
  }, [identity.enrolment?.status]);

  // OTP / cross-device auto-close. When a returning visitor verifies
  // their 6-digit code on a borrowed desktop (or any second device),
  // the auth-state-change → resolveSession flips status straight to
  // 'enrolled' without firing Step 2. If the modal is still open in
  // its awaitingLink state at that point, dismiss it so the visitor
  // sees the signed-in cockpit. No-op for the new-enrolment path
  // (modal already closed by completeEnrolment's onEnrolled callback
  // before status flips to 'enrolled').
  useEffect(() => {
    if (!IS_STRIPPED_DOWN) return;
    if (identity.enrolment?.status === 'enrolled' && enrolmentOpen) {
      setEnrolmentOpen(false);
    }
  }, [identity.enrolment?.status, enrolmentOpen]);

  // Track viewport height so the Penumbra pie can shrink to fit inside
  // <main>'s visible row (viewport − 28px hazard − header − 28vh footer,
  // minus the Intelligence Terminal and market feed that sit above the
  // pie inside main). Scaled conservatively: at ~28vh the pie always has
  // at least as much headroom above it in main as the cockpit panels
  // below it, so the doughnut is never clipped by the main/footer seam.
  const [viewportH, setViewportH] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerHeight : 900,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = (): void => setViewportH(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // clamp(160, 28vh, 260) — tops out at 260px on tall screens (down from
  // the old 300px design) so content above the pie (terminal + market
  // feed) leaves enough room for the doughnut to sit fully above the
  // footer. Floor at 160 keeps the legend readable on short viewports.
  const pieSize = Math.max(160, Math.min(260, viewportH * 0.28));

  // v1.8.1 Exit Valve · Esc closes any open modal (Mint, Verify,
  // Feedback, Oracle Guide). Individual modals also register their own
  // handlers where they need finer behaviour — this is the safety net
  // so the viewer is never stuck without a keyboard escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      setVerifyOverlay(false);
      setShowMint(false);
      setFeedbackOpen(false);
      setGuideOpen(false);
      closeBriefing();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeBriefing]);

  // ── SANDBOX Power-Up ────────────────────────────────────────────────
  // Observer (tier 1) citizens see a "VERIFY IDENTITY" button that
  // runs a 3s fake Coinbase EAS scan and then promotes them to
  // CITIZEN (tier 2, 1.5× weight) via setForcedTier. identity.tier is
  // the single source of truth — no parallel "verificationLevel" store.
  const powerUp = useCitizenPromotion();

  const cancelled = useRef(false);

  // ── Body scroll lock when drawer open ────────────────
  useEffect(() => {
    const peek = isMobile ? '76px' : '0px';
    const open = isMobile && drawerOpen ? '88vh' : peek;
    document.documentElement.style.setProperty('--drawer-height', open);
    if (isMobile && drawerOpen) document.body.style.overflow = 'hidden';
    else                         document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, drawerOpen]);

  // ── BUILD_TIME witnesses ───────────────────────────────────────────
  // Two redundant display paths so the Architect can always verify the
  // live build:
  //   1. #agoracle-build-time-raw — a plain white-on-black bar as the
  //      very first child of <body>, outside every styled component.
  //      Clipping is structurally impossible.
  //   2. .agoracle-sandbox-stamp — the existing chevron-strip pill,
  //      recolored bright red so it's unmistakable when the chevron
  //      row is visible.
  // Both pull from BUILD_STAMP_HUMAN in build-info.ts (regenerated by
  // the prebuild script on every deploy).
  useEffect(() => {
    // BUILD_STAMP_HUMAN already includes the London zone suffix
    // ("BST" during summer, "GMT" in winter) — no extra suffix here.
    const label = `BUILD_TIME: ${BUILD_STAMP_HUMAN}`;

    const raw = document.getElementById('agoracle-build-time-raw');
    if (raw) raw.textContent = label;

    const stamp = document.querySelector<HTMLElement>('.agoracle-sandbox-stamp');
    if (stamp) {
      stamp.textContent        = label;
      stamp.style.color        = '#fff';
      stamp.style.background   = '#dc2626';
      stamp.style.borderColor  = '#fca5a5';
      stamp.style.fontWeight   = '900';
      stamp.style.whiteSpace   = 'normal';
      stamp.style.maxWidth     = '100%';
      stamp.style.overflow     = 'visible';
    }
  }, []);

  // Auto-close drawer when leaving mobile breakpoint
  useEffect(() => { if (!isMobile && drawerOpen) setDrawerOpen(false); }, [isMobile, drawerOpen]);

  // ── Markets ──────────────────────────────────────────────────────────────
  // Cockpit reads the markets list from Flask's hardcoded seed at
  // engine/app.py:1500+ (legacy, captured for cleanup as I-066). Per
  // I-065 the per-market status comes from Arena Supabase via the
  // overlay so the cockpit reflects real settlement state (Flask's
  // hardcoded status='OPEN' never changes). SETTLED markets within
  // 48h of settled_at stay visible with the badge; older SETTLED
  // markets drop from the feed. Phantom-pledge protection is
  // independent: place_pledge RPC rejects status != 'OPEN' server-side.
  const fetchMarkets = useCallback(async () => {
    try {
      const r    = await fetch(`${API_BASE}/api/multi/markets?t=${Date.now()}`, { cache: 'no-store' });
      const data = (await r.json()) as MultiMarket[];
      if (cancelled.current) return;

      const overlay = await fetchMarketStatusOverlay(data.map(m => m.market_id));
      if (cancelled.current) return;

      const cutoffMs = Date.now() - 48 * 60 * 60 * 1000;
      const merged: MultiMarket[] = [];
      for (const m of data) {
        const o      = overlay.get(m.market_id);
        const status = o?.status ?? m.status;
        // I-068 Brief 2: surface oracle binding from the overlay onto
        // every merged market regardless of status — settled rows
        // still display the oracle line at muted opacity, OPEN rows
        // display it normally.
        const oracleFields = {
          oracle_display_text:     o?.oracle?.pledger_disclosure_text ?? null,
          oracle_class:            o?.oracle?.oracle_class ?? null,
          oracle_default_holdback: o?.oracle?.default_holdback_hours ?? null,
          market_holdback_hours:   o?.holdback_hours ?? null,
          challenge_deadline:      o?.challenge_deadline ?? null,
        };
        if (status === 'OPEN') {
          merged.push({ ...m, status, ...oracleFields });
        } else if (status === 'SETTLED') {
          const settledAtMs = o?.settled_at ? Date.parse(o.settled_at) : NaN;
          if (Number.isFinite(settledAtMs) && settledAtMs > cutoffMs) {
            merged.push({
              ...m,
              status,
              settled_at:      o?.settled_at ?? null,
              winning_outcome: o?.winning_outcome ?? null,
              ...oracleFields,
            });
          }
        }
        // CLOSED / CANCELLED / other → dropped (mirrors prior OPEN-only filter)
      }

      setMarkets(merged);
      setError(null);
      if (!activeId) {
        const firstOpen = merged.find(m => m.status === 'OPEN');
        if (firstOpen) setActiveId(firstOpen.market_id);
      }
    } catch (e) {
      if (!cancelled.current) setError((e as Error).message);
    }
  }, [activeId]);

  // ── Prices ───────────────────────────────────────────────────────────────
  const fetchPrices = useCallback(() => {
    if (!activeId) return;
    fetch(`${API_BASE}/api/multi/market/${activeId}/prices?t=${Date.now()}`, { cache: 'no-store' })
      .then(r => r.json())
      .then(d => {
        if (cancelled.current) return;
        // Preserve the previous array if the server burps an empty
        // payload on a heartbeat. Otherwise rawPrices flickers empty,
        // priceData empties, and the whole Pledge Insight surface
        // collapses + reflows every 5s. Only the very first fetch
        // (when no outcomes have landed yet) is allowed through empty.
        if (d.outcomes?.length) {
          setRawPrices(d.outcomes);
          if (!selectedOutcome) setSelectedOutcome(d.outcomes[0].id);
        }
      })
      .catch(() => {});
  }, [activeId, selectedOutcome]);

  useEffect(() => {
    cancelled.current = false;
    fetchMarkets();
    // Socket.io was wired here for push updates on multi_bet / vibe_update
    // / multi_market_created. The Render back end is retired for now, so
    // we rely on the 5s poller below and visibilitychange refetches to
    // avoid the WebSocket handshake errors in DevTools.
    const poll = setInterval(() => { if (!cancelled.current) { fetchMarkets(); fetchPrices(); } }, 5000);
    document.addEventListener('visibilitychange', fetchMarkets);
    return () => {
      cancelled.current = true;
      clearInterval(poll);
      document.removeEventListener('visibilitychange', fetchMarkets);
    };
  }, [fetchMarkets, fetchPrices, activeId, currentUser]);

  useEffect(() => { fetchPrices(); }, [fetchPrices]);

  // ── LOCAL feed sort (2026-05-20 brief) ───────────────────────────────────
  // Client-side re-ordering of the already-fetched market array.
  // OcVolume is computed here too (mirrors the formula inside the
  // render block) so the comparator can read a precomputed number
  // rather than re-doing the aggregate-lookup per pairwise compare.
  // Settled-sink is enforced as a partition step inside partitionAndSort,
  // not inside each comparator — so a future criterion added to
  // FEED_SORT_OPTIONS inherits the sink rule automatically.
  const sortedMarkets = useMemo(() => {
    const enriched = markets.map(m => {
      const perMarket = feedAggregates[m.market_id] ?? {};
      const ocVolume = m.outcomes.reduce((sum, o) => {
        const agg = perMarket[o.label];
        return sum + (agg ? agg.cost_total : 0);
      }, 0);
      return {
        market: m,
        settled:            m.status === 'SETTLED',
        challenge_deadline: m.challenge_deadline ?? null,
        created_at:         m.created_at ?? null,
        settled_at:         m.settled_at ?? null,
        ocVolume,
      };
    });
    return partitionAndSort(enriched, feedSortKey).map(e => e.market);
  }, [markets, feedAggregates, feedSortKey]);

  // ── Live quote ────────────────────────────────────────────────────────────
  // Always compute a local mock first so the UI stays populated even when the
  // /api/multi/quote endpoint is unavailable. If the live quote returns valid
  // JSON, it wins; otherwise the local mock holds.
  useEffect(() => {
    if (!activeId || !selectedOutcome || !shares) { setQuote(null); return; }
    const n = parseFloat(shares);
    if (isNaN(n) || n <= 0) { setQuote(null); return; }
    const outcome = priceData.find(x => x.id === selectedOutcome);
    if (!outcome) { setQuote(null); return; }

    // Session B (2026-05-13): `shares` is now OC stake (primary input)
    // per I-012 / Session B go-brief §3(f). Cost = stake directly;
    // approximate price_after via a linear bump that's similar in
    // magnitude to the RPC's actual integrated-slippage value for
    // typical pool sizes. The RPC is canonical at pledge time; this
    // is just a pre-flight indicative number.
    const localQuote = {
      cost:        n,
      price_after: Math.min(0.999, outcome.price + (n / outcome.price) / 5000),
    };
    setQuote(localQuote);

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/multi/quote?market_id=${activeId}&outcome_id=${selectedOutcome}&shares=${n}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) throw new Error('non-json');
        const d = await res.json();
        // Session B (2026-05-13): `n` is OC stake (primary input).
        // Cost = stake directly. The legacy Flask `/api/multi/quote`
        // endpoint's `price_after` is still indicative; the canonical
        // value comes from the place_pledge RPC at submission time.
        if (!cancelled.current) setQuote({
          cost:        n,
          price_after: typeof d?.price_after === 'number' ? d.price_after : localQuote.price_after,
        });
      } catch {
        // Silent fallback — local mock already shown.
      }
    })();
  }, [activeId, selectedOutcome, shares, priceData]);

  // ── Place bet — Supabase ledger (Dummy Pot) ──────────────────────────────
  // Inserts into public.pledges and decrements public.profiles.balance
  // through recordPledgeRemote. ensureProfile guarantees a profile row
  // exists before the debit (founding stipend of 500 OC on first touch).
  // Local ledger cache is updated in lockstep so the balance pill and
  // audit panel repaint without a round-trip.
  async function placeBet(overrideSlippage: boolean = false) {
    const n = parseFloat(shares);
    if (isNaN(n) || n <= 0 || !selectedO || !quote) return;
    if (!currentUser) {
      setMsg({ text: 'Connect to the Agora before pledging.', ok: false });
      return;
    }

    // Unseeded-market guard. Refuse the pledge if the operator has
    // not yet seeded this market — pledging against an empty pool
    // produces the pinned-price pattern that surfaced I-052. Soft-
    // fail (clear user-facing message); the form button is also
    // disabled below, so this is belt-and-braces.
    if (marketUnseeded) {
      setMsg({ text: 'This market is awaiting setup. Please try another.', ok: false });
      return;
    }

    // ── Trust gate ──────────────────────────────────────────────────
    // Agoracle Global pledges require Level 1 (Citizen) or higher.
    // Anonymous wallet holders hit a Verification Required overlay
    // rather than a generic error — the overlay gives them an action
    // (claim the Seal) instead of a wall. Ghost Mode bypasses this
    // because the Hub is the sandbox.
    if (identity.mode === 'wallet' && identity.tier < IdentityLevel.OBSERVER) {
      setVerifyOverlay(true);
      return;
    }

    // ── Final Quote ──────────────────────────────────────────────────
    // Session B (2026-05-13): `shares` is OC stake (primary input)
    // per I-012 / go-brief §3(f). Cost = stake directly; projected
    // payout_units = stake / displayed price (the cockpit's pre-
    // pledge approximation). The RPC computes the *actual* effective
    // price + payout_units at pledge time; we use the RPC return
    // values for the post-pledge success message.
    const lockedPrice = selectedO.price;
    if (lockedPrice <= 0) {
      setMsg({ text: 'Price unavailable — wait for the next pool refresh.', ok: false });
      return;
    }
    const lockedCost      = n;                              // OC stake
    const lockedLabel     = selectedO.label;
    // Session B Phase C (2026-05-13): tier-scaled trade_fee and
    // tier-weighted calculated_weight removed from the Pledge write.
    // Per DECISIONS.md 2026-05-13 I-046, tier mechanics are held back
    // on the Arena; the parimutuel commission per I-047 is the
    // canonical fee model and is computed server-side at settlement.
    // The legacy pledges.trade_fee / pledges.calculated_weight
    // columns continue to exist server-side for old rows; the cockpit
    // just stops writing them. Inventory §6 / §9 items 9 + 10.
    // Prophecy-multiplier underdog-boost ("mult") computation also
    // removed — the success-message render was the only consumer.
    const projectedUnits  = lockedCost / lockedPrice;       // for the Pledge object's `units` (legacy mirror)

    setBuying(true); setMsg(null);
    if (!overrideSlippage) setSlippageReject(null);

    // ── Signature Gate ───────────────────────────────────────────────
    // Wallet-mode citizens must sign a statement acknowledging the
    // pledge before it touches the Supabase ledger. Ghost-mode users
    // skip this — their identity is already scoped to the browser,
    // so a second confirmation is pure friction.
    if (identity.mode === 'wallet') {
      if (!signer) {
        setMsg({ text: 'No signer available — reconnect your wallet.', ok: false });
        setBuying(false);
        return;
      }
      const statement =
        `I, Citizen ${currentUser}, authorize a pledge of ${lockedCost.toFixed(2)} ${BALANCE_UNIT_LABEL} on ${lockedLabel}.`;
      try {
        await signer.signMessage(statement);
      } catch (e) {
        const text = e instanceof Error ? e.message : String(e);
        setMsg({ text: `Signature refused · ${text}`, ok: false });
        setBuying(false);
        return;
      }
    }

    try {
      await ensureProfile(currentUser);
      const enriched = await recordPledgeRemote({
        user_id:           currentUser,
        market_id:         activeId,
        outcome:           lockedLabel,
        units:             projectedUnits,
        price_at_pledge:   lockedPrice,
        timestamp:         new Date().toISOString(),
      }, overrideSlippage);
      // Session B (2026-05-13): post-pledge success message uses the
      // actual effective_price + slippage from the RPC return value,
      // not the form's pre-pledge approximation. That's the four-
      // price snapshot per go-brief §3(b), compactly surfaced.
      const eff      = enriched.effective_price;
      const slip     = enriched.slippage_pct;
      const effLabel = typeof eff === 'number'
        ? ` · effective price ${(eff * 100).toFixed(2)}%`
        : '';
      const slipLabel = typeof slip === 'number' && slip > 0
        ? ` · slippage +${(slip * 100).toFixed(2)}%`
        : '';
      setMsg({
        text: `Pledge recorded · ${lockedCost.toFixed(2)} OC stake on ${lockedLabel.toUpperCase()}${effLabel}${slipLabel}`,
        ok:   true,
      });
      setSlippageReject(null);
      // ── Balance Glow ───────────────────────────────────────────────
      // 1s pulse on the balance pill so the deduction feels confirmed.
      // The animation is pure CSS (see the <style> block below), we
      // just toggle the class for the duration of one cycle.
      setBalanceGlow(true);
      window.setTimeout(() => setBalanceGlow(false), 1000);
    } catch (err) {
      if (err instanceof SlippageCapError) {
        // Per go-brief §3(a) shape (iii): catch the RPC's slippage-cap
        // rejection, surface the four-price detail in a panel, offer
        // a one-click retry-with-override. No client-side pre-flight.
        setSlippageReject(err.details);
        setMsg(null);
      } else {
        const text = err instanceof Error ? err.message : String(err);
        console.error('[PLEDGE INSIGHT] pledge failed', err);
        setMsg({ text: `Pledge error · ${text}`, ok: false });
      }
    } finally {
      setBuying(false);
    }
  }

  // ── Signal normalisation: Signal = (vibe / Σvibes) − price ───────────────
  // This makes vibe and price both sum to ~1 before subtracting, so the gap
  // reads in true percentage-point terms (e.g. 26.7% vibe share − 20% price = +6.7pp).
  const totalVibe    = priceData.reduce((s, o) => s + o.composite_vibe, 0) || 1;

  // ── Compute which outcomes are "outsiders" (below threshold) ──────────────
  const totalPrice = priceData.reduce((s, o) => s + o.price, 0) || 1;
  const outsiderIds = new Set(priceData.filter(o => o.price / totalPrice < THRESHOLD).map(o => o.id));
  const outsiderOutcomes = priceData.filter(o => outsiderIds.has(o.id));

  // v2.2.7 — main-grid doughnut now uses the same PenumbraChart as the
  // MarketFocusModal. Single source of truth for the ring render means
  // the two views stay pixel-identical through any future geometry
  // tweak. Colour is the shared rowColor() used by the pledge table.
  // v2.3 — `pulse` is gone from PenumbraOutcome; the Penumbra ring
  // now derives its own vibe-scaled heartbeat from each slice's vibe
  // share (see PenumbraChart.tsx). `glowIds` still drives the per-row
  // price-flash on the pledge grid below; the two surfaces now read
  // from separate signals (sentiment vs price change).
  const penumbraOutcomes: PenumbraOutcome[] = priceData.map((o, idx) => ({
    id:    o.id,
    label: o.label,
    color: rowColor(o, idx),
    price: o.price,
    vibe:  o.composite_vibe,
  }));

  const selectedO = priceData.find(o => o.id === selectedOutcome);
  const selectedMultiplier = selectedO ? prophecyMultiplier(selectedO.price) : 1;

  // Derive prophecy alerts — normalized gap for ticker display
  const prophecyAlerts: ProphecyAlert[] = priceData
    .filter(o => isProphecy(o))
    .map(o => ({ label: o.label, gap: o.composite_vibe / totalVibe - o.price }));

  // ── Price Impact warning ────────────────────────────────────────────
  // Session B Phase C (2026-05-13): the old threshold-gated warning
  // (only showed when n >= SLIPPAGE_THRESHOLD = 200 units and the
  // linear-approximation shift > 0.1%) is removed. The quote summary
  // now always shows the actual parimutuel slippage (computed via
  // parimutuelPricing.ts) so the user sees price impact at every
  // stake, not just large ones. And the RPC's 5% cap fires hard on
  // slippage breach via the SlippageCapError catch-and-retry-override
  // path. Two layered safeguards replace the single warning.
  // Inventory §6 / §9 item 20.

  function openManifesto() { setGuideTab('manifesto'); setGuideOpen(true); }

  return (
    <>
    {/* System Briefing — mounted OUTSIDE .cockpit-shell so the mobile
        universal selector (.cockpit-main *) cannot flatten its
        position:fixed overlay into an inline block at the bottom of
        the scroll ribbon. Always mounted so the slide-in transform
        runs on first open; visibility is driven by `open`. */}
    {/* Session B Phase C (2026-05-13): the BRIEFING sidebar exposes
        the Ascent Ladder (tier system) and held-back-feature dossier.
        Per DECISIONS.md 2026-05-13 I-046, gated on the Arena. */}
    {!IS_STRIPPED_DOWN && <SystemBriefingSidebar open={briefingOpen} onClose={closeBriefing} />}

    <div className="cockpit-shell" style={{
      backgroundColor: '#020617',
      // Jailbreak: height + overflow locks deleted at source. Mobile
      // scroll is the non-negotiable priority; the desktop 100vh grid
      // can be restored from git once the iPhone is confirmed free.
      fontFamily:      'monospace',
      maxWidth:        '100vw',
      overflowAnchor:  'none',
    }}>
      <style>{`
        /* ── Short-viewport flow mode ─────────────────────────────────
           The cockpit shell pins the page to a fixed 100vh grid on tall
           displays (iMac / external monitor) so panels scroll and the
           page itself never does. On a MacBook M2 and any laptop-class
           viewport the same layout can't fit — the Penumbra pie or the
           DivergenceAlphaBox footer (Reputation Alpha + Prophecy
           Leaderboard) end up clipped by the main/footer seam.
           Below 1000px of viewport height we relax every vertical cap
           — shell height → auto, grid rows → auto, main and footer
           overflow → visible — so the entire page scrolls naturally
           and every component is reachable. The 768px minimum keeps
           the mobile breakpoint (max-width: 767px) untouched; it has
           its own dedicated layout further down. !important is
           required to beat the inline styles set in JSX. */
        @media (max-height: 1000px) and (min-width: 768px) {
          .cockpit-shell {
            height:             auto !important;
            min-height:         calc(100vh - 28px);
            grid-template-rows: auto auto auto !important;
            overflow:           visible !important;
          }
          .cockpit-main {
            overflow-y: visible !important;
            min-height: 0 !important;
          }
          .cockpit-footer {
            max-height: none    !important;
            overflow-y: visible !important;
          }
        }

        /* ── Mobile divorce (≤ 767px) ─────────────────────────────────
           The iPhone gets a long scrolling document. No grid, no flex
           height fights, no fixed positioning on main containers, no
           overflow lock anywhere. Every major card is position:relative
           and width:100%. The page is allowed to be 3000px tall if it
           needs to be — iPhone Safari scrolls it like any document. */
        @media (max-width: 767px) {
          /* Shell · block flow, no grid, no clipping. */
          .cockpit-shell {
            height:             auto !important;
            min-height:         auto !important;
            max-height:         none !important;
            display:            block !important;
            overflow:           visible !important;
            overflow-y:         visible !important;
            overflow-x:         visible !important;
            grid-template-rows: none !important;
            max-width:          100vw !important;
            position:           relative !important;
          }
          /* Main · natural block, no flex-row, consistent gutters. */
          .cockpit-main {
            display:            block !important;
            gap:                0 !important;
            padding:            16px !important;
            overflow:           visible !important;
            min-height:         0 !important;
            width:              100% !important;
            max-width:          100vw !important;
            height:             auto !important;
            max-height:         none !important;
            position:           relative !important;
          }
          /* Card spacing via margin, not flex gap. 2rem (32px) bottom
             margin on every top-level card so nothing looks like a
             single seized block — each section breathes. */
          .cockpit-main > * {
            width:              100% !important;
            max-width:          100% !important;
            margin-bottom:      2rem !important;
            position:           relative !important;
            flex:               0 0 auto !important;
          }
          .cockpit-main > *:last-child { margin-bottom: 0 !important; }
          /* Kill any tall inline min-height reservation on mobile — a
             480px or 260px minHeight (set on desktop to stop layout
             jitter during /prices polling) reads like an 80vh empty
             pane on an iPhone, which is the "dominating pane" the
             Architect reported. Natural content height wins instead. */
          .cockpit-main div {
            min-height:         0 !important;
          }
          /* Subtle visual separation + opaque card backgrounds — soft
             shadow, hairline border, and a solid theme-matching fill
             so adjacent sections read as distinct cards and nothing
             reads as a transparent overlay. */
          .cockpit-main > *:not(button):not(p):not(span) {
            border-radius:      8px !important;
            background-color:   #0a121d !important;
            box-shadow:
              0 2px 6px rgba(0, 0, 0, 0.55),
              0 0 0 1px rgba(51, 65, 85, 0.55) !important;
          }
          /* Ground every descendant of the main column — any lingering
             position:absolute / position:fixed (inlined from desktop
             styles) gets flattened to the vertical ribbon. */
          .cockpit-main *,
          .market-feed-wrapper * {
            position:           relative !important;
            top:                auto !important;
            left:               auto !important;
            right:              auto !important;
            bottom:             auto !important;
            z-index:            auto !important;
          }
          /* Decorative aria-hidden dots (diagnostic LEDs inside a
             relative parent) keep their absolute positioning. */
          .cockpit-main [aria-hidden="true"] {
            position:           absolute !important;
          }
          /* Political-odds / market feed wrapper — solid grounded
             card with a 2px BLUE debug border (per audit directive)
             so the Architect can see exactly where the political
             market table starts and ends during this pass.
             Explicit position:relative, display:block, transform:none,
             opacity:1 kill any lingering "ghost pane" state. */
          .market-feed-wrapper {
            position:           relative !important;
            display:            block !important;
            transform:          none !important;
            opacity:            1 !important;
            background:         #0a121d !important;
            border:             2px solid #2563eb !important;
            border-radius:      8px !important;
            padding:            12px !important;
            box-shadow:
              0 2px 6px rgba(0, 0, 0, 0.55),
              0 0 0 1px rgba(37, 99, 235, 0.35) !important;
          }
          .market-feed-wrapper > * {
            background:         #0e1622 !important;
            border-radius:      6px !important;
            opacity:            1 !important;
            transform:          none !important;
          }
          /* Main Action column (pie + Oracle) — 2px GREEN debug
             border (per directive) so the Architect can see exactly
             where one section ends and the next begins as they
             scroll through the ribbon. */
          .pie-column {
            border:             2px solid #22c55e !important;
            border-radius:      8px !important;
            padding:            12px !important;
            background:         #0a121d !important;
          }
          /* Universal height liberation — strip every max-height and
             open every overflow on every div / section / article /
             main / header / footer under the cockpit. No element can
             create a nested scroll container on mobile; the page is
             a single continuous scroll ribbon. */
          .cockpit-shell div,
          .cockpit-shell section,
          .cockpit-shell article,
          .cockpit-shell main,
          .cockpit-shell header,
          .cockpit-shell footer {
            max-height:         none !important;
            overflow-y:         visible !important;
            overflow-x:         visible !important;
          }
          /* Pie+drawer wrapper and its children · block column. */
          .main-grid {
            display:            block !important;
            width:              100% !important;
          }
          .main-grid > * {
            width:              100% !important;
            max-width:          100% !important;
            flex:               0 0 auto !important;
            margin-bottom:      16px !important;
            position:           relative !important;
          }
          .main-grid > *:last-child { margin-bottom: 0 !important; }
          .pie-column,
          .agora-drawer {
            flex:               0 0 auto !important;
            width:              100% !important;
            min-width:          0 !important;
            max-width:          100% !important;
            display:            block !important;
            height:             auto !important;
            max-height:         none !important;
            position:           relative !important;
          }
          /* Bottom-sheet drawer fully disabled on mobile. */
          .agora-drawer {
            transform:          none !important;
            inset:              auto !important;
            background:         transparent !important;
            border:             none !important;
            border-radius:      0 !important;
            box-shadow:         none !important;
            padding:            0 !important;
            overflow:           visible !important;
            z-index:            auto !important;
          }
          .agora-drawer-handle { display: none !important; }
          .agora-drawer-scrim  { display: none !important; }
          .agora-drawer-body {
            padding:            0 !important;
            overflow:           visible !important;
            display:            block !important;
            height:             auto !important;
            max-height:         none !important;
          }
          .agora-drawer-body > * {
            width:              100% !important;
            margin-bottom:      16px !important;
            position:           relative !important;
            flex:               0 0 auto !important;
          }
          .agora-drawer-body > *:last-child { margin-bottom: 0 !important; }
          /* Footer · flows at bottom of document, no cap. */
          .cockpit-footer {
            max-height:         none !important;
            height:             auto !important;
            overflow:           visible !important;
            padding:            16px !important;
            position:           relative !important;
            width:              100% !important;
          }
          /* 44px minimum tap target (Apple HIG / WCAG 2.5.5). */
          .cockpit-shell button:not([aria-hidden="true"]),
          .cockpit-shell select,
          .cockpit-shell input[type="text"],
          .cockpit-shell input[type="number"],
          .cockpit-shell input[type="search"] {
            min-height:         44px !important;
          }
        }

        /* ── Scroll unlock · global ───────────────────────────────────
           Per directive: kill every height/overflow lock on the root
           chain so the iPhone can scroll the page like a long
           document. overflow-y: visible means content flows into the
           document (not a scroll container) — the body / viewport
           does the scrolling. */
        html, body, #root {
          height:     auto !important;
          min-height: 100% !important;
          overflow-y: visible !important;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
        }

        @keyframes prophecy-glow {
          0%, 100% { background-color: #1a1200; border-color: #78350f; }
          50%       { background-color: #2a1e00; border-color: #fcd34d; }
        }
        .prophecy-row { animation: prophecy-glow 2s ease-in-out infinite; }

        /* ── Balance Glow: 1s pulse after a confirmed pledge. Runs on
              compositor properties only (color, text-shadow, transform)
              so it doesn't reflow the header layout around it. ─────── */
        @keyframes balance-glow {
          0%   { color: #7dd3fc; text-shadow: 0 0 0 rgba(125,211,252,0);     transform: scale(1); }
          30%  { color: #86efac; text-shadow: 0 0 16px rgba(134,239,172,0.95); transform: scale(1.08); }
          70%  { color: #86efac; text-shadow: 0 0 10px rgba(134,239,172,0.6);  transform: scale(1.03); }
          100% { color: #7dd3fc; text-shadow: 0 0 0 rgba(125,211,252,0);     transform: scale(1); }
        }
        .balance-glow {
          animation:          balance-glow 1s ease-out;
          display:            inline-block;
          transform-origin:   center;
          will-change:        transform, color, text-shadow;
        }

        /* ── Oracle Pulse: a single bloom when a Ward price moves ──────── */
        @keyframes oracle-pulse {
          0%   { box-shadow: inset 0 0 0 1px rgba(125,211,252,0);    background-color: transparent; }
          25%  { box-shadow: inset 0 0 14px 2px rgba(125,211,252,0.55); background-color: rgba(12,26,46,0.9); }
          100% { box-shadow: inset 0 0 0 1px rgba(125,211,252,0);    background-color: transparent; }
        }
        .oracle-pulse { animation: oracle-pulse 0.9s ease-out; }

        @keyframes oracle-price-flash {
          0%   { color: #7dd3fc; text-shadow: 0 0 0 rgba(125,211,252,0); }
          25%  { color: #e0f2fe; text-shadow: 0 0 10px rgba(125,211,252,0.95); }
          100% { color: #7dd3fc; text-shadow: 0 0 0 rgba(125,211,252,0); }
        }
        .oracle-price-flash { animation: oracle-price-flash 0.9s ease-out; }

        /* ── Cockpit · sidebar scroll panels ─────────────────────────────
           Inside a flex column whose parent is overflow-hidden, panels
           share the remaining height (min-height:0 unlocks shrink).
           Custom WebKit scrollbar keeps the Bloomberg terminal density;
           Firefox falls back to the thin native scrollbar. */
        .terminal-scroll {
          flex: 1 1 0;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: thin;
          scrollbar-color: #333 transparent;
        }
        .terminal-scroll::-webkit-scrollbar        { width: 6px; }
        .terminal-scroll::-webkit-scrollbar-track  { background: transparent; }
        .terminal-scroll::-webkit-scrollbar-thumb  { background: #333; border-radius: 3px; }
        .terminal-scroll::-webkit-scrollbar-thumb:hover { background: #475569; }

        /* ── v1.8 Tightening · lens select glow ──────────────────────────
           Punctuates the active reality the user is currently viewing.
           Colour is injected via the style attribute on the select; the
           glow animation is shared across lenses so flip cadence feels
           uniform. */
        @keyframes lens-active-glow {
          0%, 100% { box-shadow: 0 0 6px  var(--lens-glow, rgba(252,211,77,0.35)); }
          50%      { box-shadow: 0 0 14px var(--lens-glow, rgba(252,211,77,0.65)); }
        }
        .lens-active {
          animation: lens-active-glow 2.4s ease-in-out infinite;
        }

        /* ── v2.2 Truth Pulse · ConfidenceStar glow ──────────────────────
           Fires on the verified (gold) band. The --truth-pulse-glow
           custom property carries the intensity picked in StarStrip
           (volume-weighted), so a quiet market pulses gently while a
           high-volume gold market glows bright. Pure text-shadow so
           it never causes layout reflow. */
        @keyframes truth-pulse {
          0%, 100% { text-shadow: 0 0 4px  var(--truth-pulse-glow, rgba(252,211,77,0.25)); }
          50%      { text-shadow: 0 0 11px var(--truth-pulse-glow, rgba(252,211,77,0.55)); }
        }
        .truth-pulse {
          animation: truth-pulse 2s infinite ease-in-out;
          will-change: text-shadow;
        }

        /* ── v2.2 Lens Radiance · divergence-driven tab glow ─────────────
           Soft cyan neon that signals "this lens is hot" — mean
           divergence across its markets is above the 20% threshold.
           Applied to the LENS <select>; keyframes kept separate from
           .lens-active so the two can combine on the same element. */
        @keyframes lens-radiance {
          0%, 100% { box-shadow: 0 0 6px  rgba(125,211,252,0.35), inset 0 0 4px rgba(125,211,252,0.20); }
          50%      { box-shadow: 0 0 14px rgba(125,211,252,0.70), inset 0 0 8px rgba(125,211,252,0.35); }
        }
        .lens-radiance {
          animation: lens-radiance 2.2s infinite ease-in-out;
        }

        /* ── v2.2 Veto Purge · glitch + dissolve ─────────────────────────
           The purge fires when a market gets Sovereign-Vetoed. The main
           grid gets a 150ms RGB-split glitch (filter + transform), then
           the affected card dissolves (opacity + scale). Both classes
           are one-shot; .veto-glitch is meant to be removed after
           150ms via setTimeout. */
        @keyframes veto-glitch {
          0%    { filter: none;                                      transform: translate(0,0); }
          20%   { filter: drop-shadow(2px 0 0 #f87171) drop-shadow(-2px 0 0 #7dd3fc); transform: translate(1px,-1px); }
          40%   { filter: drop-shadow(-3px 0 0 #f87171) drop-shadow(3px 0 0 #7dd3fc); transform: translate(-1px,1px); }
          60%   { filter: drop-shadow(1px 0 0 #f87171) drop-shadow(-2px 0 0 #7dd3fc); transform: translate(2px,0); }
          80%   { filter: drop-shadow(-1px 0 0 #f87171) drop-shadow(1px 0 0 #7dd3fc);  transform: translate(0,1px); }
          100%  { filter: none;                                      transform: translate(0,0); }
        }
        .veto-glitch { animation: veto-glitch 150ms steps(5, end) 1; }

        @keyframes veto-dissolve {
          0%   { opacity: 1; transform: scale(1);     filter: blur(0); }
          60%  { opacity: 0.4; transform: scale(0.98); filter: blur(2px) saturate(1.5); }
          100% { opacity: 0; transform: scale(0.92); filter: blur(6px) saturate(0); }
        }
        .veto-dissolve {
          animation: veto-dissolve 600ms ease-in forwards;
          pointer-events: none;
        }

        /* Desktop/laptop compaction (incl. 14" MacBook split panes):
           let the system line wrap rather than overflow into neighboring
           controls when horizontal space is tight. */
        @media (max-width: 1360px) {
          .system-line-wrap {
            flex-wrap: wrap;
            row-gap: 4px;
          }
          .system-line-grow-break {
            flex-basis: 100%;
            height: 0;
          }
          .system-line-copy {
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        /* ── Cockpit · terminal density (extreme compression) ────────────
           Default body text drops to 0.85rem and H1–H6 to 1rem so every
           surface inside the cockpit reads as a financial terminal. The
           rules stay permissive (plain tag selectors, no specificity
           tricks) so inline styles still win where a component opts out. */
        body { font-size: 0.85rem; }
        h1, h2, h3, h4, h5, h6 { font-size: 1rem; margin: 0; }

        /* ── Agora drawer · handle/scrim permanently hidden ──────────────
           The mobile bottom-sheet drawer has been retired. Its CSS
           (second @media (max-width: 767px) block) was defeating the
           earlier mobile-divorce overrides on source-order ties and
           pinning .agora-drawer as position:fixed; bottom:0;
           transform:translateY(calc(100% - 76px)) — i.e. the Ghost
           Pane. Handle and scrim stay hidden at all widths so the
           inline JSX affordances never render. */
        .agora-drawer-handle { display: none !important; }
        .agora-drawer-scrim  { display: none !important; }

        /* ── Mobile-prominent BETA + Oracle Guide ─────────────────────── */
        @keyframes agora-beta-pulse {
          0%, 100% { box-shadow: 0 0 0 rgba(252,211,77,0.0); }
          50%      { box-shadow: 0 0 14px rgba(252,211,77,0.55); }
        }
        @media (max-width: 640px) {
          .agora-beta-pill {
            font-size:     12px !important;
            padding:       4px 12px !important;
            letter-spacing: 0.32em !important;
            color:         #fef3c7 !important;
            background:    rgba(252,211,77,0.22) !important;
            border-color:  #fcd34d !important;
            animation:     agora-beta-pulse 3s ease-in-out infinite;
          }
          .agora-oracle-pill {
            font-size:     13px !important;
            padding:       11px 22px 11px 18px !important;
            min-height:    44px !important;
            box-shadow:    0 0 18px rgba(124,58,237,0.55) !important;
            border-color:  #c4b5fd !important;
          }
          .agora-oracle-pill > span:first-child { font-size: 22px !important; }
        }
        @media (max-width: 380px) {
          .agora-oracle-pill {
            font-size:     10px !important;
            padding:       9px 12px 9px 10px !important;
            letter-spacing: 0.14em !important;
            gap:            6px !important;
          }
          .agora-oracle-pill > span:first-child { font-size: 16px !important; }
          .agora-beta-pill { font-size: 10px !important; padding: 3px 9px !important; letter-spacing: 0.26em !important; }
        }
      `}</style>

      {/* UserGuideModal renders the Library / Manifesto content.
          Held back per DECISIONS.md 2026-05-13 I-046. Even though the
          trigger button is gated, defensive guard here too — any
          stale openManifesto() call elsewhere produces no modal. */}
      {!IS_STRIPPED_DOWN && guideOpen && (
        <UserGuideModal onClose={() => setGuideOpen(false)} initialTab={guideTab} />
      )}

      {/* ─ COCKPIT · ROW 1: Header ─ Everything from the brand banner
            through the God Mode strip lives in this semantic header. As
            the first grid-participating child of the outer grid it maps
            to the first `auto` row — sized to its content, not flex. */}
      <header className="cockpit-header" style={{ minWidth: 0 }}>

      {/* ── Brand banner ─────────────────────────────────────────────────── */}
      <div style={{
        backgroundColor: '#1e1b4b',
        padding:         '7px 16px',
        borderBottom:    '2px solid #6366f1',
        display:         'flex',
        alignItems:      'center',
        gap:             8,
        flexWrap:        'wrap',
      }}>
        {/* Contact · Terms · Bug Report — pill cluster on the left of the
            stripe, matching the BETA pill's visual register. Contact and
            Terms are plain links (mailto: + new-tab static page); Bug
            Report opens the FeedbackModal which now drafts a mailto to
            feedback@agoracle.io rather than firing the Discord webhook. */}
        <a
          href="mailto:contact@agoracle.io?subject=Agoracle%20Arena%20contact"
          style={{
            fontFamily:     'monospace',
            fontSize:       9,
            fontWeight:     700,
            color:          '#fde68a',
            letterSpacing:  '0.18em',
            padding:        '2px 8px',
            border:         '1px solid #a16207',
            borderRadius:   999,
            background:     'rgba(161,98,7,0.18)',
            textDecoration: 'none',
            cursor:         'pointer',
            whiteSpace:     'nowrap',
          }}
        >
          CONTACT
        </a>
        <a
          href="/about.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily:     'monospace',
            fontSize:       9,
            fontWeight:     700,
            color:          '#fde68a',
            letterSpacing:  '0.18em',
            padding:        '2px 8px',
            border:         '1px solid #a16207',
            borderRadius:   999,
            background:     'rgba(161,98,7,0.18)',
            textDecoration: 'none',
            cursor:         'pointer',
            whiteSpace:     'nowrap',
          }}
        >
          ABOUT
        </a>
        <a
          href="/terms.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily:     'monospace',
            fontSize:       9,
            fontWeight:     700,
            color:          '#fde68a',
            letterSpacing:  '0.18em',
            padding:        '2px 8px',
            border:         '1px solid #a16207',
            borderRadius:   999,
            background:     'rgba(161,98,7,0.18)',
            textDecoration: 'none',
            cursor:         'pointer',
            whiteSpace:     'nowrap',
          }}
        >
          TERMS
        </a>
        <button
          onClick={() => setFeedbackOpen(true)}
          style={{
            fontFamily:    'monospace',
            fontSize:      9,
            fontWeight:    700,
            color:         '#fde68a',
            letterSpacing: '0.18em',
            padding:       '2px 8px',
            border:        '1px solid #a16207',
            borderRadius:  999,
            background:    'rgba(161,98,7,0.18)',
            cursor:        'pointer',
            whiteSpace:    'nowrap',
          }}
        >
          BUG REPORT
        </button>

        {/* Left spacer so the main logo strip stays visually centered on
            wide viewports while the version stamp floats right. */}
        <span style={{ flex: 1, minWidth: 0 }} />

        <span style={{
          fontFamily:    "'Cormorant Garamond', 'Georgia', serif",
          fontSize:      18,
          fontWeight:    500,
          color:         '#e0e7ff',
          letterSpacing: '0.02em',
        }}>
          Agoracle
        </span>
        <span className="agora-beta-pill" style={{
          fontFamily:    'monospace',
          fontSize:      9,
          fontWeight:    700,
          color:         '#fde68a',
          letterSpacing: '0.28em',
          padding:       '2px 8px',
          border:        '1px solid #a16207',
          borderRadius:  999,
          background:    'rgba(161,98,7,0.18)',
        }}>
          BETA
        </span>
        {/* Sovereign Trace — DD/MM HH:MM UTC captured at the exact
            moment the build ran (see scripts/write-build-info.js).
            Absolute proof that the live page is the latest push; any
            stale preview URL will show a different stamp. Subtle
            styling so it doesn't compete with the brand. */}
        <span
          title={`Built ${BUILD_STAMP_HUMAN} UTC · ${BUILD_SHA} · ${BUILD_BRANCH} · ${BUILD_TIME}`}
          style={{
            fontFamily:    'monospace',
            fontSize:      9,
            fontWeight:    700,
            color:         '#cbd5e1',
            opacity:       0.75,
            letterSpacing: '0.14em',
            padding:       '3px 8px',
            border:        '1px solid #334155',
            borderRadius:  3,
            background:    '#0b1220',
            userSelect:    'all',
          }}
        >
          BUILD:&nbsp;{BUILD_STAMP_HUMAN}
        </span>
        {!IS_GAME_MODE && (
          <>
            <span style={{ color: '#4338ca', fontSize: 10 }}>·</span>
            <span style={{
              fontFamily:    'monospace',
              fontSize:      10,
              fontWeight:    700,
              color:         '#c7d2fe',
              letterSpacing: '0.18em',
            }}>
              NORWICH&nbsp;·&nbsp;LOCAL ELECTION 2026&nbsp;·&nbsp;13 WARDS
            </span>
          </>
        )}

        {/* v1.8.1 Mint Isolation · the Mint entrance moved up into the
            Hazard Stripe (public/index.html) so every tier can see it and
            the brand row stays uncluttered. React listens for the
            `agoracle:open-mint` CustomEvent dispatched from that button
            — see the useEffect near the top of this component. */}
        <span style={{ flex: 1, minWidth: 0 }} />
      </div>

      {/* ── Tickertape ───────────────────────────────────────────────────── */}
      {/* GlobalTicker surfaces ProphecyAlerts, divergence_points and
          vibe_update socket subscriptions — all held back per
          DECISIONS.md 2026-05-13 I-046. Gated on the Arena. */}
      {!IS_STRIPPED_DOWN && <GlobalTicker mode="multi" prophecyAlerts={prophecyAlerts} />}

      {/* ── Profile-error banner (Supabase failure on mount) ─────────────── */}
      {profileError && (
        <div style={{
          padding:         '10px 16px',
          backgroundColor: '#2a0505',
          borderBottom:    '2px solid #dc2626',
          color:           '#fecaca',
          fontFamily:      'monospace',
          fontSize:        12,
          fontWeight:      700,
          letterSpacing:   '0.04em',
          wordBreak:       'break-word',
        }}>
          <span style={{ color: '#fca5a5', letterSpacing: '0.22em', fontSize: 10, marginRight: 8 }}>
            ⚠ PROFILE CREATE FAILED ·
          </span>
          {profileError}
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ position: 'relative', borderBottom: '1px solid #333', paddingBottom: 10, marginBottom: 0, padding: '8px 16px 10px', maxWidth: '100vw', boxSizing: 'border-box' }}>
        {/* Diagnostic dot — green when Supabase env vars resolved, red when missing */}
        <span
          title={`Supabase · URL: ${SUPABASE_DIAG.urlSource} · KEY: ${SUPABASE_DIAG.keySource}`}
          style={{
            position:        'absolute',
            top:             3,
            right:           4,
            width:           6,
            height:          6,
            borderRadius:    '50%',
            backgroundColor: SUPABASE_DIAG.connected ? '#22c55e' : '#dc2626',
            boxShadow:       SUPABASE_DIAG.connected
              ? '0 0 6px rgba(34,197,94,0.9)'
              : '0 0 6px rgba(220,38,38,0.9)',
            pointerEvents:   'auto',
            zIndex:          50,
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, rowGap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* CLASSIC VIEW link points at the legacy MainApp cockpit
                — gated when IS_GAME_MODE per the same reasoning that
                redirects /classic and /gauge to / on the Arena.
                Session B Phase C revision-3 / gate-23 Finding A. */}
            {!IS_GAME_MODE && (
              <>
                <Link to="/classic" style={{ color: '#4b6080', fontSize: 10, letterSpacing: '0.14em', textDecoration: 'none' }}>
                  ← CLASSIC VIEW
                </Link>
                <span style={{ color: '#1e293b' }}>·</span>
              </>
            )}
            <span style={{ color: '#7dd3fc', fontSize: 10, letterSpacing: '0.18em' }}>{IS_GAME_MODE ? 'MARKETS' : 'MULTI-OUTCOME MARKETS'}</span>
            <span style={{ color: '#6366f1', fontSize: 8, border: '1px solid #4c1d95', borderRadius: 3, padding: '1px 5px' }}>BETA</span>
            {identity.mode !== 'wallet' && !IS_GAME_MODE && (
              <>
                <span style={{ color: '#1e293b' }}>·</span>
                <Link
                  to="/global"
                  title="Switch to Agoracle Global (wallet)"
                  style={{
                    color:          '#a78bfa',
                    fontSize:       10,
                    letterSpacing:  '0.14em',
                    textDecoration: 'none',
                    fontFamily:     'monospace',
                    border:         '1px solid #4c1d95',
                    borderRadius:   4,
                    padding:        '2px 8px',
                  }}>
                  AGORACLE GLOBAL ↻
                </Link>
              </>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* v1.9.4 Neutral Utility · System Briefing toggle. Sits to
                the left of the Oracle Guide pill so it reads as the
                technical dossier sibling to the curated guide. Close path
                routes through closeBriefing so the session-seen flag is
                always set — otherwise a toggle-close would skip the flag
                and the briefing would auto-reappear on the next mount.

                Session B Phase C (2026-05-13): the BRIEFING button
                opens the Ascent Ladder sidebar — held-back tier
                exposure per DECISIONS.md 2026-05-13 I-046. Button
                gated on the Arena. */}
            {!IS_STRIPPED_DOWN && (
            <button
              onClick={() => {
                if (briefingOpen) {
                  closeBriefing();
                } else {
                  setBriefingOpen(true);
                }
              }}
              title={isMobile ? 'Command Drawer — tactical controls' : 'System Briefing — tier-aware operational dossier'}
              aria-label={briefingOpen ? (isMobile ? 'Close Command Drawer' : 'Close System Briefing') : (isMobile ? 'Open Command Drawer' : 'Open System Briefing')}
              aria-expanded={briefingOpen}
              style={{
                display:        'inline-flex',
                alignItems:     'center',
                gap:            6,
                background:     briefingOpen ? '#1e293b' : '#0b1220',
                border:         `1px solid ${briefingOpen ? '#fcd34d' : '#334155'}`,
                borderRadius:   4,
                color:          briefingOpen ? '#fcd34d' : '#94a3b8',
                fontFamily:     'monospace',
                fontSize:       isMobile ? 11 : 10,
                fontWeight:     700,
                letterSpacing:  '0.22em',
                padding:        isMobile ? '8px 12px' : '5px 10px',
                minHeight:      isMobile ? 40 : undefined,
                cursor:         'pointer',
                transition:     'color 0.15s, border-color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => { if (!briefingOpen) { e.currentTarget.style.color = '#fcd34d'; e.currentTarget.style.borderColor = '#78350f'; } }}
              onMouseLeave={e => { if (!briefingOpen) { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = '#334155'; } }}
            >
              <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>⌬</span>
              {isMobile ? '[ SYS ]' : 'BRIEFING'}
            </button>
            )}
            {/* Session B Phase C (2026-05-13): Oracle Guide opens the
                Library / Manifesto modal — held back per
                DECISIONS.md 2026-05-13 I-046 (Architect reclassification
                post-gate-4). Button gated on the Arena. */}
            {!IS_STRIPPED_DOWN && (
            <button onClick={() => { setGuideTab('manifesto'); setGuideOpen(true); }}
              title="Oracle Guide — Manifesto, mechanics, and the rite of settlement"
              aria-label="Open Oracle Guide"
              className="agora-oracle-pill"
              style={{
                display:        'inline-flex',
                alignItems:     'center',
                gap:             8,
                background:      'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                border:          '1px solid #a78bfa',
                borderRadius:    999,
                color:           '#faf5ff',
                fontSize:        11,
                fontWeight:      600,
                letterSpacing:   '0.18em',
                textTransform:   'uppercase',
                padding:         '6px 16px 6px 12px',
                cursor:          'pointer',
                fontFamily:      "'Inter', -apple-system, system-ui, sans-serif",
                boxShadow:       '0 0 14px rgba(124,58,237,0.35)',
                transition:      'transform 0.15s ease-out, box-shadow 0.15s, filter 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 0 22px rgba(167,139,250,0.55)'; e.currentTarget.style.filter = 'brightness(1.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)';     e.currentTarget.style.boxShadow = '0 0 14px rgba(124,58,237,0.35)'; e.currentTarget.style.filter = 'none'; }}>
              <span aria-hidden="true" style={{
                fontFamily:  "'Cormorant Garamond', 'Georgia', serif",
                fontSize:    17,
                lineHeight:  1,
                fontWeight:  500,
                fontStyle:   'italic',
                transform:   'translateY(-0.5px)',
              }}>
                ◈
              </span>
              Oracle Guide
            </button>
            )}
            {/* Architect-only tester chip. Gated on SOVEREIGN tier so
                invitees never see it — the chip itself is the entry
                point to the tester panel, and used to render
                unconditionally to every visitor. SOVEREIGN is reached
                via __agoracleForceTier(3) in DevTools console, which
                is a deliberate Architect-side incantation. (Arena
                soft-launch blocker fix, 2026-05-15.) */}
            {identity.tier === IdentityLevel.SOVEREIGN && (
              <button
                onClick={() => setGodOpen(o => !o)}
                style={{
                  background: 'none', fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em',
                  padding: '3px 9px', cursor: 'pointer', borderRadius: 4,
                  border:  `1px solid ${godOpen ? '#7f1d1d' : '#2a0a0a'}`,
                  color:   godOpen ? '#ef4444' : '#4a1010',
                }}
              >
                {godOpen ? '✕ GOD MODE' : '⚠ GOD MODE'}
              </button>
            )}
            <span style={{ color: '#64748b', fontSize: 9 }}>AS</span>
            {identityPill}
            {/* Arena enrolment / sign-in affordance — only on Arena,
                only when the user is still on a Ghost identity.
                Enrolled identities are surfaced by the pill itself
                (@username). Label covers both first-time enrolment
                and returning-on-a-new-device sign-in: same magic-link
                flow either way; the post-click step is conditional
                on whether the email already maps to an enrolled
                profile. Brief 2026-05-14 §4 + gate-9 follow-up
                2026-05-15. */}
            {IS_STRIPPED_DOWN && identity.enrolment?.status === 'ghost' && (
              <button
                onClick={() => setEnrolmentOpen(true)}
                title="Enrol or sign in via magic-link"
                style={{
                  background:     'transparent',
                  border:         '1px solid #6366f1',
                  borderRadius:   12,
                  color:          '#c7d2fe',
                  fontSize:       10,
                  letterSpacing:  '0.12em',
                  padding:        '3px 10px',
                  cursor:         'pointer',
                  fontFamily:     'monospace',
                  textTransform:  'uppercase',
                }}
              >
                Enrol / Sign in
              </button>
            )}
            {identity.connected ? (
              <span
                className={balanceGlow ? 'balance-glow' : ''}
                style={{ color: '#7dd3fc', fontSize: 12, fontWeight: 700 }}>
                {balance.toFixed(2)} {BALANCE_UNIT_LABEL}
              </span>
            ) : (
              <span style={{
                fontFamily:    "'Cormorant Garamond', 'Georgia', serif",
                fontStyle:     'italic',
                color:         '#fcd34d',
                fontSize:      12,
                letterSpacing: '0.02em',
              }}>
                ⚱ Connect to the Agora
              </span>
            )}
          </div>
        </div>

        {/* ── System_Line (v2.2 §4 · Terminal Density) ──────────────
              Single monospace strip that consolidates Build / Wallet /
              Reputation. Replaces the old two-row header (Trust Ladder
              used to live on its own line beneath the brand banner).
              Everything sits inline so the cockpit header collapses to
              one scan-line — version on the left, tier/balance on the
              right, dot-separators in the terminal tradition. ─────── */}
        {(() => {
          const meta = TIER_META[identity.tier];
          return (
            <div className="system-line-wrap" style={{
              display:        'flex',
              alignItems:     'center',
              gap:             8,
              padding:         '3px 14px 4px',
              fontFamily:      'monospace',
              fontSize:        9,
              letterSpacing:   '0.14em',
              color:           '#475569',
              borderTop:       '1px solid #111827',
              backgroundColor: '#020617',
            }}>
              <span style={{ color: '#334155' }}>◈ SYSTEM</span>
              <span style={{ color: '#1e293b' }}>·</span>
              <span style={{ color: '#64748b' }}>
                {APP_VERSION}/{BUILD_TAG}
              </span>
              <span style={{ color: '#1e293b' }}>·</span>
              <span style={{ color: '#64748b' }}>
                WALLET {identity.connected ? `${balance.toFixed(2)} ${BALANCE_UNIT_LABEL}` : 'OFFLINE'}
              </span>
              <span className="system-line-grow-break" style={{ flex: 1 }} />
              <span style={{ fontSize: 10, lineHeight: 1 }}>{meta.icon}</span>
              <span style={{ color: meta.color, fontWeight: 700 }}>
                {meta.label}
              </span>
              <span style={{ color: meta.border }}>·</span>
              <span className="system-line-copy" style={{ color: meta.accent }}>
                {identity.tier === IdentityLevel.SEEKER
                  ? meta.sublabel
                  : `${meta.sublabel} · ${identity.label}`}
              </span>
              <button onClick={openManifesto}
                title="Open Oracle Manifesto"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: meta.color, fontSize: 9, padding: 0, fontFamily: 'monospace',
                  letterSpacing: '0.14em',
                }}>
                ↗
              </button>
            </div>
          );
        })()}
      </div>

      {/* ── God Mode panel (full-width, above grid, no overlap risk) ──────
            Same SOVEREIGN gate as the chip — defence in depth in case
            godOpen ever flips through a side-channel (legacy
            localStorage, React Devtools, etc). */}
      {identity.tier === IdentityLevel.SOVEREIGN && godOpen && activeId && (
        <div style={{ padding: '0 16px 10px', borderBottom: '1px solid #2a0a0a' }}>
          <GodModePanel marketId={activeId} currentUser={currentUser} multiMode />
        </div>
      )}

      </header>

      {/* ─ COCKPIT · ROW 2: Main ─ View switcher, LENS strip, and the
            live markets surface. Caps to the flex 1fr grid row and
            scrolls internally so the page itself never scrolls. */}
      <main
        className={`cockpit-main${vetoGlitchOn ? ' veto-glitch' : ''}`}
        style={{
          padding:      '8px 16px',
          overflowY:    'auto',
          overflowX:    'hidden',
          minHeight:    0,
        }}
      >

      {error && <p style={{ color: 'rgba(239,68,68,0.7)', fontSize: 11, marginBottom: 10 }}>{error}</p>}

      {/* ── View-mode switcher: Markets / History ────────────────────────── */}
      <div style={{
        display:       'flex',
        gap:           0,
        borderBottom:  '1px solid #333',
        marginBottom:  12,
      }}>
        {/* HISTORY tab subtitle "Sentinels of the Ward" + the panel
            it opens both surface held-back vibe-history content.
            Phase C revision-1 gated the panel's internals (oracle
            entries pre-filtered, ACTIVITY LOG header, no GOSSIP
            filter button); gate-23 caught that the tab itself in
            the nav strip wasn't gated. Phase C revision-3 /
            gate-23 Finding B: gate the HISTORY tab when
            IS_STRIPPED_DOWN, leaving MARKETS as the only nav
            option on the Arena. */}
        {(IS_STRIPPED_DOWN
          ? [{ id: 'markets', label: 'MARKETS', sub: 'Live trading', accent: '#7dd3fc' }] as const
          : [
              { id: 'markets', label: 'MARKETS', sub: 'Live trading', accent: '#7dd3fc' },
              { id: 'history', label: 'HISTORY', sub: 'Sentinels of the Ward', accent: '#c084fc' },
            ] as const
        ).map(v => {
          const active = view === v.id;
          return (
            <button key={v.id} onClick={() => setView(v.id)}
              style={{
                background:   'none',
                border:       'none',
                cursor:       'pointer',
                padding:      '10px 18px 12px',
                marginRight:  8,
                borderBottom: `2px solid ${active ? v.accent : 'transparent'}`,
                color:        active ? '#e2e8f0' : '#475569',
                fontFamily:   'monospace',
                textAlign:    'left',
                transition:   'color 0.15s, border-bottom-color 0.15s',
              }}>
              <div style={{ fontSize: 11, letterSpacing: '0.18em', fontWeight: 700 }}>
                {v.label}
              </div>
              <div style={{
                fontSize: 8, letterSpacing: '0.18em', marginTop: 2,
                color: active ? v.accent : '#334155',
              }}>
                {v.sub}
              </div>
            </button>
          );
        })}
      </div>

      {/* ── HISTORY view ───────────────────────────────────────────────────
          Belt-and-brace gate alongside the nav-strip gate above (gate-23
          Finding B). If the user's `view` state was set to 'history'
          before IS_STRIPPED_DOWN took effect (cached client-side state
          across deploys), the nav tab won't render the entry but the
          mount could still fire. The IS_STRIPPED_DOWN guard here closes
          that edge case — when stripped, the HistoryPanel never mounts
          even if view === 'history'. */}
      {!IS_STRIPPED_DOWN && view === 'history' && (
        <HistoryPanel
          apiBase={API_BASE}
          marketId={activeId}
          marketTitle={markets.find(m => m.market_id === activeId)?.title}
        />
      )}

      {/* ── LENS strip ───────────────────────────────────────────────────
          Terminal-bar lens control. Source of truth for both the Social
          Pulse ticker (drawer) and the PledgeCardGrid (main pane). When
          lens !== LOCAL the main pane swaps from the live AMM feed to
          the sandbox MarketCard pool per §5. */}
      {view === 'markets' && (
        <div style={{
          display:         'flex',
          alignItems:      'center',
          gap:             10,
          padding:         '8px 14px',
          marginBottom:    12,
          borderRadius:    6,
          backgroundColor: '#0b1220',
          border:          '1px solid #333',
          fontFamily:      'monospace',
        }}>
          <span style={{ color: '#475569', fontSize: 9, letterSpacing: '0.22em', fontWeight: 700 }}>
            ◈ INTELLIGENCE TERMINAL
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ color: '#334155', fontSize: 8, letterSpacing: '0.22em' }}>LENS</span>
          <select
            value={lens}
            onChange={e => setLens(e.target.value as RegionLens)}
            disabled={IS_GAME_MODE}
            className={averageDivergenceForLens(lens) > 0.20 ? 'lens-active lens-radiance' : 'lens-active'}
            title={
              averageDivergenceForLens(lens) > 0.20
                ? `Lens divergence ${(averageDivergenceForLens(lens) * 100).toFixed(1)}% — signal hot`
                : undefined
            }
            style={{
              backgroundColor: '#0f172a',
              color:           '#e2e8f0',
              border:          `1px solid ${lens === 'LOCAL' ? '#7dd3fc' : lens === 'GLOBAL' ? '#fcd34d' : lens === 'ASIA' ? '#f472b6' : '#a78bfa'}`,
              borderRadius:    4,
              padding:         '5px 10px',
              fontFamily:      'monospace',
              fontSize:        10,
              letterSpacing:   '0.12em',
              fontWeight:      700,
              cursor:          IS_GAME_MODE ? 'default' : 'pointer',
              opacity:         IS_GAME_MODE ? 0.85 : 1,
              outline:         'none',
              // CSS custom property consumed by .lens-active keyframes —
              // the Active glow pulses in the lens's own accent colour.
              ['--lens-glow' as any]:
                lens === 'LOCAL'  ? 'rgba(125,211,252,0.55)' :
                lens === 'GLOBAL' ? 'rgba(252,211,77,0.55)'  :
                lens === 'ASIA'   ? 'rgba(244,114,182,0.55)' :
                                    'rgba(167,139,250,0.55)',
            }}
          >
            {LENS_OPTIONS
              .filter(opt => !IS_GAME_MODE || opt.value === 'LOCAL')
              .map(opt => (
              <option key={opt.value} value={opt.value} style={{ backgroundColor: '#0f172a', color: '#e2e8f0' }}>
                {opt.label}
              </option>
            ))}
          </select>
          {lens !== 'LOCAL' && (
            <span style={{
              color:           '#78350f',
              fontSize:        8,
              letterSpacing:   '0.22em',
              fontWeight:      800,
              padding:         '2px 8px',
              border:          '1px solid #78350f',
              borderRadius:    3,
              backgroundColor: 'rgba(120,53,15,0.12)',
            }}>
              PREVIEW MODE
            </span>
          )}

          {/* Mint entrance moved to the Hazard Stripe (v1.8.1 Mint
              Isolation) — the LENS strip is now purely a lens/preview
              control so the cockpit reads cleaner. The ⚒ Mint Market
              button lives in public/index.html and reaches React via
              the `agoracle:open-mint` CustomEvent. */}
        </div>
      )}

      {!IS_STRIPPED_DOWN && showMint && <MintModal onClose={() => setShowMint(false)} />}

      {/* Arena enrolment modals — Arena-only. EnrolmentModal opens
          at Step 1 from the cockpit ENROL button; Step 2 auto-opens
          when identity.enrolment.status === 'pending_enrolment'
          after the magic-link round-trip. GhostClaimModal mounts
          on enrolment success when a local Ghost id remains and
          self-dismisses if there's nothing to claim. */}
      {IS_STRIPPED_DOWN && enrolmentOpen && (
        <EnrolmentModal
          initialStep={
            identity.enrolment?.status === 'pending_enrolment' ? 2 : 1
          }
          authUserId={identity.enrolment?.authUserId}
          authEmail={identity.enrolment?.authEmail}
          onClose={() => setEnrolmentOpen(false)}
          onEnrolled={(enrolledId) => {
            setEnrolmentOpen(false);
            // Re-read the enrolled profile so identity.id flips to
            // the enrolled id immediately — useUserDP, pledge writes,
            // realtime subscriptions all reach for identity.id.
            dispatchEnrolmentRefresh();

            // Ghost id resolution — read the React snapshot first;
            // fall back to raw localStorage. The snapshot path is
            // normally correct (GhostIdentityProvider seeds ghostId
            // synchronously at mount and preserves it through every
            // enrolment-state transition), but localStorage is the
            // literal source of truth. Belt-and-brace for the
            // 2026-05-16 gate-4 finding where the modal failed to
            // surface.
            let ghostId: string | undefined = identity.enrolment?.ghostId;
            let ghostIdSource: 'snapshot' | 'localStorage' | 'none' = 'snapshot';
            if (!ghostId && typeof window !== 'undefined') {
              try {
                const ls = window.localStorage.getItem('agoracle_ghost_id');
                if (ls) { ghostId = ls; ghostIdSource = 'localStorage'; }
                else    { ghostIdSource = 'none'; }
              } catch { ghostIdSource = 'none'; }
            }
            console.log('[MultiPage.onEnrolled] trigger evaluation', {
              enrolledId,
              snapshotStatus:   identity.enrolment?.status,
              snapshotGhostId:  identity.enrolment?.ghostId,
              resolvedGhostId:  ghostId,
              ghostIdSource,
              differs:          ghostId ? ghostId !== enrolledId : null,
            });
            if (ghostId && ghostId !== enrolledId) {
              console.log('[MultiPage.onEnrolled] mounting GhostClaimModal');
              setPendingGhostClaim({ ghostId, enrolledId });
            } else {
              console.log('[MultiPage.onEnrolled] NOT mounting GhostClaimModal — no ghostId or matches enrolledId');
            }
          }}
        />
      )}

      {IS_STRIPPED_DOWN && pendingGhostClaim && (
        <GhostClaimModal
          ghostProfileId={pendingGhostClaim.ghostId}
          enrolledProfileId={pendingGhostClaim.enrolledId}
          onDone={() => {
            setPendingGhostClaim(null);
            // identity.id is already on the enrolled id (dispatched
            // when EnrolmentModal completed); the refresh here picks
            // up the new identity_claims row so effective_owner
            // queries running off identity.id reflect the claim.
            dispatchEnrolmentRefresh();
          }}
        />
      )}

      {/* ── v2.2 Market Feed (replaces tabs) ─────────────────────────────
          Dense rows — one per open market. Macro data only; clicking a
          row opens the Focus Modal and syncs activeId so the existing
          prices/quote pipeline targets it. */}
      {view === 'markets' && lens === 'LOCAL' && (<>
      <div className="market-feed-wrapper relative block w-full opacity-100 bg-slate-950" style={{ marginBottom: 32 }}>
        {/* ── Feed sort control (2026-05-20 brief) ─────────────────────
            Sits above the pending rail so the citizen sees it before
            scanning rows. Pure client-side re-ordering of the already-
            fetched market array — no fetch on change. Pending rows
            (rendered below) stay at the top regardless of sort;
            settled rows always sink below active ones. */}
        <div
          style={{
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'flex-end',
            gap:             8,
            padding:         '6px 10px',
            borderBottom:    '1px solid #1e293b',
          }}
        >
          <label
            htmlFor="feed-sort"
            style={{
              color:         '#64748b',
              fontSize:      9,
              letterSpacing: '0.22em',
              fontFamily:    'monospace',
              fontWeight:    700,
            }}
          >
            SORT
          </label>
          <select
            id="feed-sort"
            value={feedSortKey}
            onChange={e => setFeedSortKey(e.target.value as FeedSortKey)}
            style={{
              backgroundColor: '#0f172a',
              color:           '#e2e8f0',
              border:          '1px solid #475569',
              borderRadius:    4,
              padding:         '4px 8px',
              fontFamily:      'monospace',
              fontSize:        10,
              letterSpacing:   '0.10em',
              fontWeight:      700,
              cursor:          'pointer',
              outline:         'none',
            }}
          >
            {FEED_SORT_OPTIONS.map(opt => (
              <option
                key={opt.value}
                value={opt.value}
                style={{ backgroundColor: '#0f172a', color: '#e2e8f0' }}
              >
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {/* v2.3.1 — PENDING_VOUCH rail. Pending Minted markets surface
            here as STABILIZING rows above the live ACTIVE feed, so the
            citizen sees the pipeline before they see the tradable
            slate. Pledging is suppressed on these rows (disabled at
            the MarketFeedRow level). */}
        {pendingMintedRows.map(m => {
          // 2026-05-21 feed-i006-and-breakpoint brief: feed-row switch
          // is gated on the wider `isNarrow` (<1024 px) signal so
          // iPad portrait (820/834 px) gets the TacticalMarketCard
          // path instead of the cramped MarketFeedRow grid. iPad
          // landscape (1024 px) stays on the desktop layout.
          if (isNarrow) {
            const canVouch =
              identity.tier >= IdentityLevel.CITIZEN &&
              !!identity.id &&
              identity.id !== m.creatorId &&
              !m.alreadyVouched;
            return (
              <TacticalMarketCard
                key={`pending:${m.id}`}
                marketId={m.id}
                title={m.title}
                lens={m.lens}
                ocVolume={0}
                pending={true}
                vouchCount={m.vouchCount}
                vouchTarget={m.vouchTarget}
                canVouch={canVouch}
                onAction={handleVouchPending}
              />
            );
          }
          return (
            <MarketFeedRow
              key={`pending:${m.id}`}
              marketId={m.id}
              title={m.title}
              lens={m.lens}
              ocVolume={0}
              pending={true}
              vouchCount={m.vouchCount}
              vouchTarget={m.vouchTarget}
              onClick={() => { /* locked — pledging suppressed until ACTIVE */ }}
            />
          );
        })}
        {sortedMarkets.map(m => {
          // Live parimutuel pool + per-outcome prices, read from the
          // **market-scoped** aggregates map. The earlier 2026-05-21
          // commit applied the right formula (`prePledgePrice`) but on
          // collided inputs — it looked up `globalAggregates[o.label]`,
          // the bare-label cross-market roll-up, so any two markets
          // sharing an outcome label ("The Field", "Hold at 3.75%",
          // "Yes" / "No") read each other's stake. UEFA's row showed
          // "The Field @ 0.50" / "Inter @ 0.16" because Field summed
          // every market's Field and Inter summed every market's Inter,
          // not UEFA's own. Reading `feedAggregates[m.market_id]?.[o.label]`
          // gives this row its OWN market's totals; row, badges, panel
          // and focus view finally agree.
          const perMarket = feedAggregates[m.market_id] ?? {};
          const ocVolume = m.outcomes.reduce((sum, o) => {
            const agg = perMarket[o.label];
            return sum + (agg ? agg.cost_total : 0);
          }, 0);
          const nOutcomes = m.outcomes.length;
          const priced = m.outcomes.map((o, i) => {
            const agg = perMarket[o.label];
            const p_i = agg ? agg.cost_total : 0;
            return {
              id:      o.id,
              label:   o.label,
              price:   prePledgePrice(p_i, ocVolume, nOutcomes),
              color:   rowColor(
                { id: o.id, label: o.label, price: 0, seed_vibe: 0, composite_vibe: 0, q: o.q },
                i,
              ),
              isField: isField(o),
            };
          });
          // Favourite/outsider derivation reuses computePenumbraCorners —
          // the same helper that drives the corner badges (Penumbra
          // Part C). Single source of truth across row, badges, and
          // OUTCOME panel; they cannot diverge by construction. The
          // helper picks favourite = highest live price (Field
          // eligible), outsider = lowest live price among NAMED
          // outcomes (Field excluded).
          const cornerInputs: CornerOutcome[] = priced.map(p => ({
            label:   p.label,
            price:   p.price,
            color:   p.color,
            isField: p.isField,
          }));
          const corners  = computePenumbraCorners(cornerInputs);
          const favorite = corners.favourite ? priced.find(p => p.label === corners.favourite!.label) : undefined;
          const outsider = corners.outsider  ? priced.find(p => p.label === corners.outsider!.label)  : undefined;
          const onActivate = (id: string) => {
            setActiveId(id);
            if (id !== activeId) {
              setRawPrices([]); setSelectedOutcome('');
              setMsg(null); setOthersOpen(false);
            }
            setFocusMarketId(id);
          };
          const isSettled = m.status === 'SETTLED';
          // 2026-05-21 feed-i006-and-breakpoint brief: switch gated on
          // `isNarrow` (<1024 px) so iPad portrait (820/834 px) lands
          // on the TacticalMarketCard rather than the cramped desktop
          // grid that overflowed in the screenshots.
          if (isNarrow) {
            return (
              <TacticalMarketCard
                key={m.market_id}
                marketId={m.market_id}
                title={m.title}
                lens="LOCAL"
                ocVolume={ocVolume}
                favorite={favorite}
                outsider={outsider}
                outcomes={priced}
                active={m.market_id === activeId}
                settled={isSettled}
                winningOutcome={m.winning_outcome ?? null}
                settledAt={m.settled_at ?? null}
                oracleDisplayText={m.oracle_display_text ?? undefined}
                oracleClassLabel={oracleClassLabel(m.oracle_class) || undefined}
                holdbackHoursOverride={m.market_holdback_hours ?? undefined}
                onAction={onActivate}
              />
            );
          }
          return (
            <MarketFeedRow
              key={m.market_id}
              marketId={m.market_id}
              title={m.title}
              lens="LOCAL"
              ocVolume={ocVolume}
              favorite={favorite}
              outsider={outsider}
              active={m.market_id === activeId}
              settled={isSettled}
              winningOutcome={m.winning_outcome ?? null}
              settledAt={m.settled_at ?? null}
              oracleDisplayText={m.oracle_display_text ?? undefined}
              oracleClassLabel={oracleClassLabel(m.oracle_class) || undefined}
              holdbackHoursOverride={m.market_holdback_hours ?? undefined}
              onClick={onActivate}
            />
          );
        })}
      </div>

      {/* ── v2.2 Focus Modal — Penumbra chart + metadata overlay. The
            pledging surface below stays behind the backdrop; dismissing
            the modal returns the citizen to the 3-col trading view.
            Penumbra outcomes are built directly from priceData so the
            dual-ring shows live price vs live vibe share. */}
      {/* MarketFocusModal composes PenumbraChart + a Penumbra-vibe
          metadata strip. Per DECISIONS.md 2026-05-13 I-046, gated on
          the Arena — clicking a market row still calls setActiveId
          (so the inline pledge form updates), but no modal opens.
          The inline form is the only pledge surface on the Arena. */}
      {(() => {
        if (IS_STRIPPED_DOWN) return null;
        if (!focusMarketId) return null;
        const focused = markets.find(m => m.market_id === focusMarketId);
        if (!focused) return null;
        return (
          <MarketFocusModal
            open={true}
            onClose={() => setFocusMarketId(null)}
            title={focused.title}
            lens="LOCAL"
            outcomes={penumbraOutcomes}
            metadata={{
              resolutionHierarchy: 'Human Oracle → Dispute → Sovereign',
              vouchCount:          priceData.reduce((s, o) => s + (o.units_pledged ?? 0), 0),
              creatorId:           'Root Architect',
              status:              focused.status,
              // I-068 Brief 2 — oracle binding for the new RESOLUTION
              // row. Effective holdback is the override if set,
              // otherwise the registry default. Falsy oracle_display_text
              // (system-audit) leaves the row suppressed in
              // MarketFocusModal.
              oracleDisplayText:   focused.oracle_display_text ?? undefined,
              oracleClassLabel:    oracleClassLabel(focused.oracle_class) || undefined,
              oracleClass:         focused.oracle_class ?? undefined,
              holdbackHours:       focused.market_holdback_hours ?? focused.oracle_default_holdback ?? undefined,
            }}
            winningOutcomeLabel={focused.status === 'SETTLED' ? (focused.winning_outcome ?? null) : null}
          >
            <div style={{
              display:       'flex',
              flexDirection: 'column',
              gap:           8,
              padding:       '10px 12px',
              background:    '#07101c',
              border:        '1px solid #1e293b',
              borderRadius:  6,
              color:         '#94a3b8',
              fontSize:      11,
              letterSpacing: '0.08em',
            }}>
              <span style={{ color: '#64748b', fontSize: 8, letterSpacing: '0.22em' }}>
                EXECUTION SURFACE
              </span>
              <span>
                Close this modal to access the pledge form and Oracle signal panel below.
              </span>
              <button
                type="button"
                onClick={() => setFocusMarketId(null)}
                style={{
                  alignSelf:     'flex-start',
                  marginTop:     4,
                  padding:       '7px 14px',
                  borderRadius:  5,
                  border:        '1px solid #166534',
                  background:    '#14532d',
                  color:         '#86efac',
                  fontSize:      10,
                  letterSpacing: '0.18em',
                  fontFamily:    'monospace',
                  cursor:        'pointer',
                }}
              >
                ⟶ CONTINUE TO TRADING SURFACE
              </button>
            </div>
          </MarketFocusModal>
        );
      })()}

      {/* ── Main grid ───────────────────────────────────────────────────── */}
      {activeId && (
        <div className="main-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>

          {/* ── Col 1: Pie + Oracle (Oracle bed) ───────────────────────── */}
          <div className="pie-column" style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Pie slot · responsive square, capped at 250 px. The
                PenumbraChart is passed size=250 and the surrounding
                `.penumbra-host` CSS in index.css forces its inner
                SVG and positioned overlays to scale to the container
                width, so on narrow iPhones the chart stays legible
                instead of shrinking to the pre-Naked-Flow pieSize
                clamp. */}
            {/* Session B Phase C (2026-05-13): PenumbraChart is the
                Penumbra surface — held back per DECISIONS.md
                2026-05-13 I-046.
                Pre-rollout regression fix (2026-05-16): the entire
                chart slot used to collapse on Arena, leaving the
                centre pane empty. The intent was for the inner
                price-share doughnut to keep rendering — only the
                outer vibe ring + Border-of-Reality are held back.
                Restored by passing noPenumbra={IS_STRIPPED_DOWN}
                so the chart still mounts and the inner ring still
                slices proportional to price share. */}
            <div className="penumbra-host w-full max-w-[250px] aspect-square mx-auto" style={{ position: 'relative' }}>
                {penumbraOutcomes.length > 0 && (() => {
                  // 2026-05-20 brief Part A: feed the centre rest-state.
                  // The component swaps to per-outcome readout on slice
                  // hover internally; the parent only needs to supply
                  // the rest strings (onHover left unwired — nothing
                  // outside the chart reacts to slice hover on this
                  // surface).
                  const activeMarket = markets.find(x => x.market_id === activeId);
                  const poolTotal = priceData.reduce((s, o) => s + (o.cost_pledged ?? 0), 0);
                  const centerTop    = `TOTAL POOL · ${Math.round(poolTotal)} OC`;
                  const centerBottom = formatCloseCountdown(activeMarket?.challenge_deadline);
                  const winningOutcomeLabel =
                    activeMarket?.status === 'SETTLED'
                      ? (activeMarket.winning_outcome ?? null)
                      : null;
                  // 2026-05-20 brief Part C: bottom-corner stat badges.
                  // Field-class outcomes are excluded from the outsider
                  // calc inside the helper — the local isField() probe
                  // (id /^field$/i || label /the\s*field/i) is the
                  // source of truth used everywhere else in this file.
                  const cornerInputs: CornerOutcome[] = priceData.map((o, i) => ({
                    label:   o.label,
                    price:   o.price,
                    color:   rowColor(o, i),
                    isField: isField(o),
                  }));
                  const corners = computePenumbraCorners(cornerInputs);
                  return (
                    <>
                      <PenumbraChart
                        outcomes={penumbraOutcomes}
                        size={250}
                        noPenumbra={IS_STRIPPED_DOWN}
                        centerTop={centerTop}
                        centerBottom={centerBottom}
                        winningOutcomeLabel={winningOutcomeLabel}
                      />
                      {corners.favourite && (
                        <div style={{
                          position:      'absolute',
                          bottom:        4,
                          left:          4,
                          pointerEvents: 'none',
                          display:       'flex',
                          flexDirection: 'column',
                          alignItems:    'flex-start',
                          gap:           1,
                          maxWidth:      110,
                        }}>
                          <span style={{
                            color:         '#475569',
                            fontSize:      7,
                            letterSpacing: '0.20em',
                            fontFamily:    'monospace',
                            fontWeight:    700,
                          }}>FAVOURITE</span>
                          <div style={{
                            display:    'flex',
                            alignItems: 'center',
                            gap:        4,
                            maxWidth:   110,
                          }}>
                            <span style={{
                              width:           6,
                              height:          6,
                              borderRadius:    1,
                              backgroundColor: corners.favourite.color,
                              flexShrink:      0,
                            }} />
                            <span style={{
                              flex:         '1 1 auto',
                              minWidth:     0,
                              overflow:     'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace:   'nowrap',
                              color:        '#cbd5e1',
                              fontSize:     10,
                              fontFamily:   'monospace',
                            }}>{corners.favourite.label}</span>
                            <span style={{
                              flex:       '0 0 auto',
                              color:      '#cbd5e1',
                              fontSize:   10,
                              fontFamily: 'monospace',
                              fontWeight: 700,
                            }}>{corners.favourite.pct}%</span>
                          </div>
                        </div>
                      )}
                      {corners.outsider && (
                        <div style={{
                          position:      'absolute',
                          bottom:        4,
                          right:         4,
                          pointerEvents: 'none',
                          display:       'flex',
                          flexDirection: 'column',
                          alignItems:    'flex-end',
                          gap:           1,
                          maxWidth:      110,
                          textAlign:     'right',
                        }}>
                          <span style={{
                            color:         '#475569',
                            fontSize:      7,
                            letterSpacing: '0.20em',
                            fontFamily:    'monospace',
                            fontWeight:    700,
                          }}>OUTSIDER</span>
                          <div style={{
                            display:    'flex',
                            alignItems: 'center',
                            gap:        4,
                            maxWidth:   110,
                          }}>
                            <span style={{
                              flex:       '0 0 auto',
                              color:      '#cbd5e1',
                              fontSize:   10,
                              fontFamily: 'monospace',
                              fontWeight: 700,
                            }}>{corners.outsider.pct}%</span>
                            <span style={{
                              flex:         '1 1 auto',
                              minWidth:     0,
                              overflow:     'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace:   'nowrap',
                              color:        '#cbd5e1',
                              fontSize:     10,
                              fontFamily:   'monospace',
                              textAlign:    'right',
                            }}>{corners.outsider.label}</span>
                            <span style={{
                              width:           6,
                              height:          6,
                              borderRadius:    1,
                              backgroundColor: corners.outsider.color,
                              flexShrink:      0,
                            }} />
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
                {penumbraOutcomes.length === 0 && (
                  <div style={{
                    position:   'absolute',
                    inset:      0,
                    display:    'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color:      '#334155',
                    fontSize:   11,
                    pointerEvents: 'none',
                  }}>Loading chart…</div>
                )}
            </div>

            {/* ── Others / Outsiders panel ─────────────────────────────── */}
            {othersOpen && outsiderOutcomes.length > 0 && (
              <div style={{ backgroundColor: '#120d00', border: '1px solid #78350f', borderRadius: 8,
                padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <p style={{ color: '#f59e0b', fontSize: 9, letterSpacing: '0.18em', margin: 0 }}>
                    ⚡ THE OUTSIDERS
                  </p>
                  <button onClick={() => setOthersOpen(false)}
                    style={{ background: 'none', border: 'none', color: '#78350f', cursor: 'pointer', fontSize: 12 }}>✕</button>
                </div>
                <p style={{ color: '#92400e', fontSize: 9, lineHeight: 1.5, margin: '0 0 10px' }}>
                  These outcomes are below 5% — high multiplier, high risk.
                  Click to select for betting.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {outsiderOutcomes.map(o => {
                    const mult  = prophecyMultiplier(o.price);
                    const proph = isProphecy(o);
                    const sel   = selectedOutcome === o.id;
                    return (
                      <div key={o.id}
                        onClick={() => { setSelectedOutcome(o.id); setOthersOpen(false); }}
                        style={{
                          display:         'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding:         '7px 10px', borderRadius: 5, cursor: 'pointer',
                          border:          `1px solid ${sel ? '#fcd34d' : proph ? '#78350f' : '#333'}`,
                          backgroundColor: sel ? '#2a1e00' : proph ? '#120d00' : '#0f172a',
                        }}>
                        <div>
                          <span style={{ color: proph ? '#fcd34d' : '#e2e8f0', fontSize: 11 }}>
                            {proph && '⚡ '}{o.label}
                          </span>
                          {proph && (
                            <div style={{ color: '#92400e', fontSize: 8, marginTop: 2 }}>
                              PROPHECY ALERT — Vibe {(o.composite_vibe * 100).toFixed(0)}% vs Price {(o.price * 100).toFixed(1)}%
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                          <div style={{ color: '#fcd34d', fontSize: 11, fontWeight: 700 }}>{mult.toFixed(1)}×</div>
                          <div style={{ color: '#78350f', fontSize: 8 }}>BONUS</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* OraclePanel hosts vibe submission — held back per
                DECISIONS.md 2026-05-13 I-046 (Penumbra Chart's signal
                source is the vibe form). The PenumbraChart's outer
                Penumbra ring stays mounted but goes flat-baseline
                naturally when no vibes are being submitted. */}
            {!IS_STRIPPED_DOWN && (
              <OraclePanel
                marketId={activeId}
                outcomes={priceData.map(o => ({ id: o.id, label: o.label }))}
                currentUser={currentUser}
                onVibeSubmitted={() => fetchPrices()}
              />
            )}
          </div>

          {/* ── Col 2: Trading column — slides up over Oracle on mobile ─ */}
          <div className={`agora-drawer ${drawerOpen ? 'open' : ''}`}
               style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 8 }}>

            {/* Mobile-only handle. Tap to expand/collapse. */}
            <button
              type="button"
              className="agora-drawer-handle"
              onClick={() => setDrawerOpen(o => !o)}
              aria-expanded={drawerOpen}
              aria-label={drawerOpen ? 'Collapse trading drawer' : 'Open trading drawer'}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 14 }}>
                <span style={{
                  fontFamily:    'monospace',
                  fontSize:      9,
                  letterSpacing: '0.28em',
                  color:         '#a78bfa',
                  fontWeight:    700,
                }}>
                  ◈ AGORA · TRADE
                </span>
                <span style={{
                  fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                  fontSize:   15,
                  fontStyle:  'italic',
                  color:      '#e2e8f0',
                  lineHeight: 1.2,
                }}>
                  {selectedO ? selectedO.label : 'Select an outcome'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 14 }}>
                {selectedO && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 14, color: '#7dd3fc', fontWeight: 700 }}>
                      {(selectedO.price * 100).toFixed(1)}%
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#475569', letterSpacing: '0.18em' }}>
                      PRICE
                    </div>
                  </div>
                )}
                <span style={{
                  display:        'inline-flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  width:          40,
                  height:         40,
                  borderRadius:   '50%',
                  background:     drawerOpen ? '#0f172a' : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                  border:         `1px solid ${drawerOpen ? '#334155' : '#a78bfa'}`,
                  color:          drawerOpen ? '#94a3b8' : '#faf5ff',
                  fontSize:       16,
                  boxShadow:      drawerOpen ? 'none' : '0 0 14px rgba(124,58,237,0.45)',
                  transition:     'transform 0.2s',
                  transform:      drawerOpen ? 'rotate(180deg)' : 'rotate(0)',
                }}>
                  ↑
                </span>
              </div>
            </button>

            <div className="agora-drawer-body">

            {/* Outcome table. Never unmounts — the container reserves a
                fixed min-height whether priceData has arrived or not,
                and an empty state is shown at opacity 0.55 so the
                drawer (and everything below it in the document) doesn't
                shift when the /prices poll refreshes. This was the
                sync-jump trigger: a momentarily empty priceData would
                collapse the table, pull Pledge Insight up, shrink the
                right column, and drag the DP explainer up with it. */}
            <div style={{ backgroundColor: '#0f172a', border: '1px solid #333', borderRadius: 8, overflow: 'hidden', minHeight: 260, contain: 'layout' }}>
              {priceData.length === 0 && (
                <div style={{
                  padding:    '80px 14px',
                  textAlign:  'center',
                  color:      '#334155',
                  fontSize:   10,
                  opacity:    0.55,
                  letterSpacing: '0.18em',
                  fontFamily: 'monospace',
                }}>
                  AWAITING WARD PRICES…
                </div>
              )}
              {priceData.length > 0 && (
              <div style={{ background: 'transparent' }}>
                <div style={{ padding: '8px 14px', borderBottom: '1px solid #333',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#475569', fontSize: 9, letterSpacing: '0.16em' }}>OUTCOME</span>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                    <span style={{ color: '#475569', fontSize: 9, letterSpacing: '0.16em', width: 50, textAlign: 'right' }}>PRICE</span>
                    {/* Session B Phase C revision (2026-05-13 post-
                        gate-23): VIBE / SIGNAL / BONUS column headers
                        all surface held-back mechanics — vibe submission,
                        divergence signal, Discovery Bonus / Prophecy
                        Multiplier. Per the inventory-miss correction in
                        §8 of the inventory document, gate the headers
                        alongside the cells below. */}
                    {!IS_STRIPPED_DOWN && (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, width: 50, justifyContent: 'flex-end' }}>
                          <span style={{ color: '#475569', fontSize: 9, letterSpacing: '0.16em' }}>VIBE</span>
                          <InfoTooltip
                            title="Oracle Vibe"
                            body="The composite sentiment score (0–1) from community oracle submissions, weighted by each user's DP accuracy score. Higher vibe = stronger positive signal."
                            accent="#f59e0b"
                            direction="below"
                            onLearnMore={openManifesto}
                          />
                        </div>
                        <span style={{ color: '#475569', fontSize: 9, letterSpacing: '0.16em', width: 50, textAlign: 'right' }}>SIGNAL</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, width: 64, justifyContent: 'flex-end' }}>
                          <span style={{ color: '#475569', fontSize: 9, letterSpacing: '0.16em' }}>BONUS</span>
                          <InfoTooltip
                            title="Prophecy Multiplier"
                            body="DP bonus for underdog bets. Formula: 1 + 1÷(Price×10). A 5% longshot earns 3× more DP than a 50% favourite. Rewards information accuracy on hard calls."
                            accent="#fcd34d"
                            direction="below"
                            onLearnMore={openManifesto}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {priceData.map((o, i) => {
                  const gap   = (o.composite_vibe / totalVibe) - o.price; // normalized pp
                  const bull  = gap > 0.005;   // > 0.5pp
                  const bear  = gap < -0.005;
                  const mult  = prophecyMultiplier(o.price);
                  const proph = isProphecy(o);
                  const big   = mult >= 2;
                  const glow = glowIds.has(o.id);
                  return (
                    <div key={o.id}
                      className={`${proph ? 'prophecy-row' : ''}${glow ? ' oracle-pulse' : ''}`.trim()}
                      onClick={() => setSelectedOutcome(o.id)}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '7px 14px', borderBottom: '1px solid #0f172a', cursor: 'pointer',
                        backgroundColor: proph ? undefined : selectedOutcome === o.id ? '#0c1a2e' : 'transparent',
                        border: proph ? undefined : selectedOutcome === o.id ? '1px solid #1e3a5a' : undefined,
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: '1 1 auto' }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2,
                          backgroundColor: rowColor(o, i) }} />
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ color: proph ? '#fcd34d' : '#e2e8f0', fontSize: 11 }}>
                            {proph && '⚡ '}{o.label}
                          </span>
                          <span style={{ color: '#475569', fontSize: 8, letterSpacing: '0.1em', fontFamily: 'monospace', marginTop: 1 }}>
                            TOTAL STAKED · {(o.cost_pledged ?? 0).toFixed(0)} OC
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 14, fontFamily: 'monospace', alignItems: 'center' }}>
                        {/* Session B Phase C revision (2026-05-13 post-
                            gate-23): inline price-share bar restores the
                            visual representation of pool composition that
                            the Penumbra chart removal left missing. The
                            bar fills to (price × 100)% of column width,
                            colored to match the outcome's row dot. In
                            empty-pool conditions all bars sit at 1/n by
                            construction (since pre_pledge_price returns
                            1/n when t = 0). When pools fill, bar widths
                            diverge and the parimutuel consensus becomes
                            visible at a glance — inventory §8 addition,
                            path B from earlier inventory discussion. */}
                        <div style={{ width: 50, textAlign: 'right' }}>
                          <span
                            className={glow ? 'oracle-price-flash' : ''}
                            style={{ color: '#7dd3fc', fontSize: 11 }}>
                            {(o.price * 100).toFixed(1)}%
                          </span>
                          <div style={{
                            height:          2,
                            marginTop:       2,
                            backgroundColor: '#0c1a2e',
                            borderRadius:    1,
                            overflow:        'hidden',
                          }}>
                            <div style={{
                              height:          '100%',
                              width:           `${Math.max(0, Math.min(100, o.price * 100))}%`,
                              backgroundColor: rowColor(o, i),
                              transition:      'width 0.3s ease-out',
                            }} />
                          </div>
                        </div>
                        {/* VIBE / SIGNAL / BONUS cells — held back per
                            inventory §8 addition. Gated together with
                            the headers above. */}
                        {!IS_STRIPPED_DOWN && (
                          <>
                            <span style={{ color: '#f59e0b', fontSize: 11, width: 50, textAlign: 'right' }}>
                              {(o.composite_vibe * 100).toFixed(0)}%
                            </span>
                            <span style={{ fontSize: 10, width: 50, textAlign: 'right',
                              color: bull ? '#34d399' : bear ? '#f87171' : '#475569' }}>
                              {bull ? `↑ +${(gap*100).toFixed(1)}%` : bear ? `↓ ${(gap*100).toFixed(1)}%` : '—'}
                            </span>
                            <span style={{ fontSize: big ? 10 : 9, width: 64, textAlign: 'right', fontWeight: big ? 700 : 400,
                              color: big ? '#fcd34d' : '#475569' }}>
                              {big ? `${mult.toFixed(1)}× BONUS!` : `${mult.toFixed(1)}×`}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>

            {/* Bet form ── PLEDGE INSIGHT
                 Strict minHeight so the card doesn't change size when
                 the 5s heartbeat refreshes prices, nor when the Price
                 Impact warning / quote summary / message toggle. The
                 value is tuned to fit the fully-populated layout:
                 header + select + prophecy badge + truth units +
                 price impact + quote summary + button + success line.
                 contain:'layout' scopes reflows to the card itself so
                 child mutations never propagate up the grid. */}
            <div style={{
              backgroundColor: '#0f172a',
              border:          `1px solid ${marketSettled ? '#475569' : '#333'}`,
              borderRadius:    8,
              padding:         '14px 16px',
              minHeight:       480,
              contain:         'layout',
              cursor:          marketSettled ? 'not-allowed' : undefined,
            }}>
              <p style={{ color: '#475569', fontSize: 9, letterSpacing: '0.18em', margin: '0 0 12px' }}>PLEDGE INSIGHT</p>
              {/* I-065 settled-market banner. Pledging is closed; the
                  RPC enforces it, this banner explains it. Rendered
                  ABOVE the form so the form's greyed-out controls have
                  a visible reason. */}
              {marketSettled && (
                <div style={{
                  marginBottom:  12,
                  padding:       '10px 12px',
                  borderRadius:  6,
                  border:        '1px solid #475569',
                  background:    'rgba(71,85,105,0.12)',
                  color:         '#cbd5e1',
                  fontFamily:    'monospace',
                  fontSize:      11,
                  letterSpacing: '0.06em',
                  lineHeight:    1.4,
                }}>
                  <div style={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.22em', fontSize: 9, marginBottom: 4 }}>
                    ⊘ MARKET SETTLED · PLEDGING CLOSED
                  </div>
                  <div>
                    {settledWinner ? <><strong style={{ color: '#e2e8f0' }}>{settledWinner}</strong> won.</> : 'This market has been resolved.'}
                    {' '}New pledges cannot be placed on a settled market.
                  </div>
                </div>
              )}
              <div style={{
                opacity:       marketSettled ? 0.35 : 1,
                filter:        marketSettled ? 'grayscale(0.8)' : undefined,
                pointerEvents: marketSettled ? 'none' : undefined,
                transition:    'opacity 0.15s ease',
              }}>
              <label style={{ display: 'block', marginBottom: 10 }}>
                <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.14em', display: 'block', marginBottom: 5 }}>OUTCOME</span>
                <select value={selectedOutcome} onChange={e => setSelectedOutcome(e.target.value)}
                  style={{ width: '100%', backgroundColor: '#1e293b', border: '1px solid #334155',
                    borderRadius: 5, color: '#e2e8f0', fontSize: 11, padding: '7px 10px',
                    fontFamily: 'monospace', outline: 'none', cursor: 'pointer' }}>
                  {priceData.map(o => (
                    <option key={o.id} value={o.id}>{o.label}  ({(o.price*100).toFixed(2)}%)</option>
                  ))}
                </select>
              </label>

              {/* Prophecy multiplier badge — held back per
                  DECISIONS.md 2026-05-13 I-046. Per the §8 inventory
                  miss caught in gate-23, the belt-and-brace return-1
                  on prophecyMultiplier() makes the value "1.00×" but
                  the row JSX (label + InfoTooltip + value) still
                  renders without an explicit gate. Mount-level gate
                  closes the surface. */}
              {!IS_STRIPPED_DOWN && selectedO && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 10, padding: '5px 10px',
                  backgroundColor: selectedMultiplier >= 2 ? '#1a1200' : '#0c1a2e',
                  border: `1px solid ${selectedMultiplier >= 2 ? '#78350f' : '#1e3a5a'}`,
                  borderRadius: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.12em' }}>PROPHECY MULTIPLIER</span>
                    <InfoTooltip
                      title="Prophecy Multiplier"
                      body="Underdog bonus on Divergence Points. Formula: 1 + 1÷(Price×10). Backing a 5% longshot earns 3× more DP than a 50% favourite — rewarding the courage to bet against the consensus."
                      accent="#fcd34d"
                      direction="above"
                      onLearnMore={openManifesto}
                    />
                  </div>
                  <span style={{ color: selectedMultiplier >= 2 ? '#fcd34d' : '#475569', fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {selectedMultiplier.toFixed(2)}×{selectedMultiplier >= 2 ? ' BONUS!' : ''}
                  </span>
                </div>
              )}

              <label style={{ display: 'block', marginBottom: 12 }}>
                <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.14em', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 5 }}>
                  OC STAKE
                  <InfoTooltip
                    title="OC stake"
                    body="How much OC you're staking. Not a fixed-odds bet — your stake joins a shared pool; if your outcome wins you take a proportional share of it, settled when the market resolves."
                    accent="#a78bfa"
                    direction="above"
                    onLearnMore={() => window.open('/about.html', '_blank', 'noopener,noreferrer')}
                    learnMoreLabel="How payouts work"
                  />
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={shares}
                  onChange={e => {
                    setShares(e.target.value.replace(/[^0-9]/g, ''));
                    if (slippageReject) setSlippageReject(null);
                  }}
                  placeholder="e.g. 10"
                  style={{ width: '100%', backgroundColor: '#1e293b', border: '1px solid #334155',
                    borderRadius: 5, color: '#e2e8f0', fontSize: 12, padding: '7px 10px',
                    fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }} />
              </label>

              {/* Quote summary. Always shown so the refetch cycle
                  doesn't punch a hole in the layout. tabular-nums
                  keeps digit widths locked while the values tick.
                  Session B Phase C (2026-05-13): slippage and price-
                  after-trade now use the parimutuelPricing.ts module
                  (mirror of the RPC's revised PL/pgSQL math) instead
                  of the old linear-approximation. This eliminates the
                  gate-4 discrepancy where the form showed +22.54%
                  while the RPC computed NULL in discovery territory. */}
              {quote && (() => {
                // Pre-flight parimutuel slippage using local aggregates.
                // p_i = stake on the selected outcome, T = total pool
                // across all outcomes, s = user's OC stake input,
                // n = number of outcomes for this market.
                //
                // 2026-05-14: `aggregates` is now market-scoped
                // (`useMarketOutcomeAggregates(activeId)` above), which
                // closes I-006 for this surface and for the outcome
                // row above. The trade surface and pledge form preview
                // therefore read from the same per-market aggregate
                // and compute via the same parimutuel function, so
                // their displayed pre-pledge prices match to 1dp by
                // construction.
                const n_outcomes = priceData.length || 2;
                const p_i        = selectedO ? (aggregates[selectedO.label]?.cost_total ?? 0) : 0;
                const t          = priceData.reduce((sum, o) => sum + (aggregates[o.label]?.cost_total ?? 0), 0);
                const s          = quote.cost;
                let pre  = 0, eff = 0, post = 0;
                let slipPct: number | null = 0;
                try {
                  pre  = prePledgePrice(p_i, t, n_outcomes);
                  eff  = s > 0 ? computeEffectivePrice(p_i, s, t, n_outcomes) : pre;
                  post = postPledgePrice(p_i, s, t);
                  slipPct = parimutuelSlippagePct(pre, eff, n_outcomes);
                } catch {
                  // Invalid input (e.g., s <= 0) — fall through with zeros.
                }
                // Display values: % for prices, fractional for slip.
                const slippage  = slipPct === null ? 0 : Math.abs(slipPct) * 100;
                const slipColor = slippage < 1 ? '#34d399' : slippage < 3 ? '#fbbf24' : '#f87171';
                return (
                  <div style={{ backgroundColor: '#0c1a2e', border: '1px solid #1e3a5a', borderRadius: 5,
                    padding: '8px 12px', marginBottom: 10, fontVariantNumeric: 'tabular-nums' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#64748b', fontSize: 10 }}>Cost</span>
                      <span style={{ color: '#7dd3fc', fontSize: 12, fontWeight: 700 }}>{quote.cost.toFixed(2)} OC</span>
                    </div>
                    {/* Session B Phase C revision (2026-05-13 post-
                        gate-23): when t = 0 (empty pool), the
                        mathematical postPledgePrice(0, s, 0) = s/s = 1.0
                        renders as "100.00%" — accurate but operationally
                        misleading. Architect direction: reframe for the
                        empty-pool case rather than show the literal
                        value. The reframe communicates the actual state
                        ("first stake") and what to expect ("price will
                        settle as the pool fills"). */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#64748b', fontSize: 10 }}>Price after trade</span>
                      {t === 0 ? (
                        <span style={{ color: '#94a3b8', fontSize: 10, fontStyle: 'italic', textAlign: 'right' }}>
                          first stake · settles as pool fills
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: 11 }}>{(post * 100).toFixed(2)}%</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ color: '#64748b', fontSize: 10 }}>Price impact</span>
                        <InfoTooltip
                          title="Price impact"
                          body="How much your pledge shifts the pool's share for this outcome. A large pledge into a small pool moves the share more. Green = small shift, Yellow = moderate, Red = large — your pledge is a big part of the pool."
                          accent="#8b5cf6"
                          direction="above"
                        />
                      </div>
                      <span style={{ color: slipColor, fontSize: 10, fontWeight: 600 }}>
                        +{slippage.toFixed(2)}%
                      </span>
                    </div>
                    {/* "Payout if this wins" — the pledger's share of the
                        whole pool if the chosen outcome wins, at the
                        current pool, net of commission. Reuses the
                        post-pledge pool share (`post`) already in scope:
                        `payoutUnits(s, post) = s / post = (s × pool_after)
                        / staked_on_mine_after` — i.e. stake ÷ winning-side
                        share = the winner's slice of the after-pledge
                        pool. `post` is the right basis (not `eff`) for a
                        displayed projection — the user is asking "if I
                        win at the current pool, what's my cut" and `post`
                        is the pool-share answer; `eff` is the RPC's
                        integrated-slippage unit-accounting price (used
                        for payout_units credited at pledge time, not for
                        forward-looking display).
                        Commission branches per mode to mirror the
                        COMMISSION line:
                          • Arena (IS_STRIPPED_DOWN): 2% on the total pool
                            at settlement → flat 2% off gross.
                          • Live: 3% on net winnings, winner only →
                            commission applies to (gross − stake), so the
                            displayed net = gross − 0.03 × (gross − s).
                        Empty-pool (`t === 0`) reframes to match the
                        "Price after trade" empty-pool treatment — a
                        literal `s / 1.0 = s` reads as "you get your stake
                        back" which misrepresents the parimutuel state. */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ color: '#64748b', fontSize: 10 }}>Payout if this wins</span>
                        <InfoTooltip
                          title="Payout if this wins"
                          body="If this outcome wins, your share of the whole pool at the current pool size, after commission. It's not fixed odds — the pool is shared, so your payout falls if more is pledged on this outcome and rises if more is pledged on the others. The final figure is set when the market settles."
                          accent="#8b5cf6"
                          direction="above"
                        />
                      </div>
                      {t === 0 || s <= 0 || post <= 0 ? (
                        <span style={{ color: '#94a3b8', fontSize: 10, fontStyle: 'italic', textAlign: 'right' }}>
                          first stake · payout settles as pool fills
                        </span>
                      ) : (() => {
                        const gross = payoutUnits(s, post);
                        const net   = IS_STRIPPED_DOWN
                          ? gross * 0.98                               // Arena: 2% on the total pool
                          : gross - 0.03 * Math.max(0, gross - s);     // Live: 3% on net winnings, winner only
                        return (
                          <span style={{ color: '#86efac', fontSize: 11, fontWeight: 700 }}>
                            {net.toFixed(2)} OC
                          </span>
                        );
                      })()}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, borderTop: '1px solid #1e3a5a', paddingTop: 6, flexWrap: 'wrap', rowGap: 4 }}>
                      <span style={{ color: '#475569', fontSize: 9, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>COMMISSION</span>
                      <span style={{ color: '#475569', fontSize: 9, textAlign: 'right', flex: '1 1 auto', minWidth: 0 }}>
                        {IS_STRIPPED_DOWN
                          ? '2% on the total pool at settlement'
                          : '3% on net winnings · winner only'}
                      </span>
                    </div>
                    {/* Tier-scaled "PROTOCOL COST" block exposes the
                        verified-identity rebate (Citizen+). Per
                        DECISIONS.md 2026-05-13 I-046 (held-back tier
                        exposure), hide on Arena; render on Live. */}
                    {!IS_STRIPPED_DOWN && quote && (() => {
                      const feeRate  = tradeFeeForTier(identity.tier);
                      const feeCost  = quote.cost * feeRate;
                      // Citizen + Sovereign share the reduced-fee rail —
                      // violet accent signals the verified-identity tier.
                      const verified = identity.tier >= IdentityLevel.CITIZEN;
                      const accent   = verified ? '#a78bfa' : '#fbbf24';
                      return (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, borderTop: '1px solid #1e3a5a', paddingTop: 6, flexWrap: 'wrap', rowGap: 4 }}>
                          <span style={{ color: accent, fontSize: 9, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>PROTOCOL COST</span>
                          <span style={{ color: accent, fontSize: 9, textAlign: 'right', flex: '1 1 auto', minWidth: 0, fontVariantNumeric: 'tabular-nums' }}>
                            {(feeRate * 100).toFixed(1)}% · {feeCost.toFixed(2)} OC
                            {verified && (
                              <span style={{ color: '#94a3b8', marginLeft: 6 }}>· Verified-rate</span>
                            )}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
              {/* ── SANDBOX Power-Up panel ─────────────────────────────────
                    Observer citizens (tier 1) see a "VERIFY IDENTITY"
                    CTA with a fake 3s Coinbase EAS bar. CITIZEN/SOVEREIGN
                    citizens see a compact "verified" confirmation chip.
                    Pure UI; nothing touches the chain.

                    Session B (2026-05-13): the entire Power-Up panel
                    exposes Citizen / Sovereign tier mechanics and
                    references the "Oracle Weight" multiplier. Per
                    DECISIONS.md 2026-05-13 I-046 (held-back tier
                    exposure), gate the whole block behind
                    IS_STRIPPED_DOWN so neither the verify-CTA nor
                    the verified-chip surfaces on the Arena. */}
              {!IS_STRIPPED_DOWN && (identity.tier < IdentityLevel.CITIZEN ? (
                <div style={{
                  marginTop:    10,
                  padding:      '12px 14px',
                  borderRadius: 8,
                  border:       '1px solid #a16207',
                  background:   'linear-gradient(90deg, rgba(161,98,7,0.18), rgba(161,98,7,0.04))',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', rowGap: 8 }}>
                    <div style={{ fontFamily: 'monospace' }}>
                      <div style={{ fontSize: 10, color: '#fcd34d', letterSpacing: '0.18em' }}>IDENTITY POWER-UP</div>
                      <div style={{ fontSize: 9, color: '#b45309', letterSpacing: '0.10em', marginTop: 2 }}>
                        Verify to become a Citizen · 1.5× Oracle Weight
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        const promoted = await powerUp.promoteToCitizen(identity.id ?? undefined);
                        if (promoted) {
                          setMsg({
                            text: 'Identity Boosted! Your Truth Units now carry 50% more weight.',
                            ok:   true,
                          });
                        }
                      }}
                      disabled={powerUp.activating}
                      style={{
                        padding:       '8px 14px',
                        borderRadius:  6,
                        border:        '1px solid #d97706',
                        background:    powerUp.activating ? '#422006' : '#78350f',
                        color:         '#fde68a',
                        fontFamily:    'monospace',
                        fontSize:      10,
                        letterSpacing: '0.16em',
                        cursor:        powerUp.activating ? 'not-allowed' : 'pointer',
                        whiteSpace:    'nowrap',
                      }}>
                      {powerUp.activating ? 'VERIFYING…' : '🚀 VERIFY IDENTITY'}
                    </button>
                  </div>
                  {powerUp.activating && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 8, color: '#fbbf24', letterSpacing: '0.22em', marginBottom: 4, fontFamily: 'monospace' }}>
                        COINBASE EAS · BASE SEPOLIA
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: '#1a0f00', overflow: 'hidden' }}>
                        <div style={{
                          height:     '100%',
                          width:      `${powerUp.progress}%`,
                          background: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
                          transition: 'width 50ms linear',
                        }} />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  marginTop:      10,
                  padding:        '10px 14px',
                  borderRadius:   8,
                  border:         '1px solid #059669',
                  background:     'rgba(16,185,129,0.10)',
                  color:          '#6ee7b7',
                  fontFamily:     'monospace',
                  fontSize:       10,
                  letterSpacing:  '0.14em',
                  display:        'flex',
                  justifyContent: 'space-between',
                  alignItems:     'center',
                  gap:            10,
                  flexWrap:       'wrap',
                }}>
                  <span>
                    ✅ IDENTITY VERIFIED · {weightMultiplierForTier(identity.tier).toFixed(1)}× WEIGHT
                  </span>
                  <span style={{ fontSize: 9, color: '#34d399', letterSpacing: '0.18em' }}>
                    {identity.tier === IdentityLevel.SOVEREIGN ? 'SOVEREIGN' : 'COINBASE EAS'}
                  </span>
                </div>
              ))}
              {/* ── Verification gate (inline) ─────────────────────────────
                    Seeker wallets (tier 0) on /global see the VERIFY
                    HUMANITY button in place of the pledge CTA. Observer
                    and above pledge normally; tier-scaled weight is
                    applied downstream. */}
              {(() => {
                const blocked  = identity.mode === 'wallet' && identity.tier < IdentityLevel.OBSERVER;
                const feeRate  = tradeFeeForTier(identity.tier);
                const feeCost  = quote ? quote.cost * feeRate : 0;
                const disabled = buying || !selectedOutcome || !quote || marketUnseeded || marketSettled;
                if (blocked) {
                  return <IdentitySealButton />;
                }
                // Session B (2026-05-13): `shares` is now OC stake
                // primary input per I-012. Stake = quote.cost (= shares
                // directly) with a safeShares fallback for the cold
                // render before quote computes.
                const numericShares = parseFloat(shares);
                const safeShares    = Number.isFinite(numericShares) ? numericShares : 0;
                const tierMult      = weightMultiplierForTier(identity.tier);
                const stake         = quote ? quote.cost : safeShares;
                const oracleWeight  = stake * tierMult;
                const boosted       = identity.tier >= IdentityLevel.CITIZEN;
                return (
                  <>
                    {/* Tier-multiplier breakdown ("Your Oracle Weight")
                        exposes Citizen / Sovereign tier mechanics. Per
                        DECISIONS.md 2026-05-13 I-046, tier-system
                        exposure is held back from the user-facing
                        surface; the panel only renders when the flag
                        is off (i.e. on Live, where tier surfaces are
                        intended). */}
                    {!IS_STRIPPED_DOWN && (
                      <div style={{
                        marginTop:          8,
                        marginBottom:       6,
                        padding:            '7px 10px',
                        borderRadius:       6,
                        background:         boosted ? 'rgba(16,185,129,0.08)' : '#0c1a2e',
                        border:             boosted ? '1px solid #059669' : '1px solid #1e3a5a',
                        display:            'flex',
                        justifyContent:     'space-between',
                        alignItems:         'center',
                        fontFamily:         'monospace',
                        fontSize:           10,
                        letterSpacing:      '0.12em',
                        fontVariantNumeric: 'tabular-nums',
                        gap:                8,
                        flexWrap:           'wrap',
                        rowGap:             4,
                      }}>
                        <span style={{ color: '#64748b' }}>YOUR ORACLE WEIGHT</span>
                        <span style={{ color: boosted ? '#6ee7b7' : '#cbd5e1' }}>
                          {stake.toFixed(2)} × {tierMult.toFixed(1)} = {oracleWeight.toFixed(2)} TRUTH UNITS
                        </span>
                      </div>
                    )}
                    {/* Unseeded-market guard banner. Renders only when
                        the seed probe has affirmatively returned zero
                        operator-seed rows for the active market. The
                        submit button below is also disabled in that
                        state; this banner explains why so the user
                        can move on rather than re-clicking. Soft-fail
                        per the 2026-05-14 brief §3. */}
                    {marketUnseeded && (
                      <div
                        title="The operator has not yet seeded this market with starting liquidity. Pledges are refused until seeding completes."
                        style={{
                          marginTop:     6,
                          marginBottom:  6,
                          padding:       '8px 10px',
                          borderRadius:  6,
                          border:        '1px solid #b45309',
                          background:    'rgba(180,83,9,0.10)',
                          color:         '#fbbf24',
                          fontFamily:    'monospace',
                          fontSize:      10,
                          letterSpacing: '0.10em',
                          lineHeight:    1.5,
                        }}>
                        <span style={{ color: '#fb923c', fontWeight: 700, letterSpacing: '0.18em' }}>⚠ AWAITING SETUP · </span>
                        This market is awaiting setup. Please try another.
                      </div>
                    )}
                    <button
                      onClick={() => placeBet()}
                      disabled={disabled}
                      title={marketUnseeded ? 'This market is awaiting setup. Please try another.' : undefined}
                      style={{
                        width:              '100%',
                        padding:            '9px',
                        borderRadius:       6,
                        border:             '1px solid #166534',
                        backgroundColor:    buying || !quote ? '#0d2a1a' : '#14532d',
                        color:              buying || !quote ? '#3a6a4a' : '#86efac',
                        fontSize:           11,
                        letterSpacing:      '0.12em',
                        cursor:             disabled ? 'not-allowed' : 'pointer',
                        fontFamily:         'monospace',
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                      {buying ? 'PLEDGING…'
                        : marketUnseeded
                          ? 'AWAITING MARKET SETUP'
                        : quote
                          ? (identity.mode === 'wallet'
                              ? `PLEDGE · ${quote.cost.toFixed(2)} OC STAKE · ${selectedO?.label ?? ''}  (+ ${feeCost.toFixed(2)} OC FEE)`
                              : `PLEDGE · ${quote.cost.toFixed(2)} OC STAKE · ${selectedO?.label ?? ''}`)
                          : 'ENTER OC STAKE'}
                    </button>
                    {/* Session B (2026-05-13) §3(a) shape (iii):
                        slippage-cap rejection panel. Shows the four-
                        price detail + "Pledge with override" button
                        when the RPC raised SlippageCapError. Cleared
                        on successful pledge or input change. */}
                    {slippageReject && (
                      <div style={{
                        marginTop:    8,
                        padding:      '10px 12px',
                        borderRadius: 6,
                        border:       '1px solid #b45309',
                        background:   'rgba(180,83,9,0.12)',
                        fontFamily:   'monospace',
                        fontSize:     10,
                        color:        '#fbbf24',
                        lineHeight:   1.5,
                      }}>
                        <div style={{ fontWeight: 700, marginBottom: 4, letterSpacing: '0.08em' }}>
                          SLIPPAGE EXCEEDS CAP
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 6 }}>
                          <span>Pre-pledge price</span>
                          <span style={{ textAlign: 'right' }}>{(slippageReject.pre_pledge_price * 100).toFixed(2)}%</span>
                          <span>Effective price</span>
                          <span style={{ textAlign: 'right' }}>{(slippageReject.effective_price * 100).toFixed(2)}%</span>
                          <span>Post-pledge price</span>
                          <span style={{ textAlign: 'right' }}>{(slippageReject.post_pledge_price * 100).toFixed(2)}%</span>
                          <span>Slippage</span>
                          <span style={{ textAlign: 'right', color: '#fb923c' }}>
                            +{(slippageReject.slippage_pct * 100).toFixed(2)}% (cap {(slippageReject.slippage_cap * 100).toFixed(0)}%)
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => placeBet(true)}
                            style={{
                              flex:             '1 1 auto',
                              padding:          '6px 10px',
                              borderRadius:     5,
                              border:           '1px solid #b45309',
                              backgroundColor:  '#78350f',
                              color:            '#fde68a',
                              fontSize:         9,
                              letterSpacing:    '0.12em',
                              cursor:           'pointer',
                              fontFamily:       'monospace',
                            }}>
                            PLEDGE WITH OVERRIDE
                          </button>
                          <button
                            onClick={() => setSlippageReject(null)}
                            style={{
                              flex:             '0 0 auto',
                              padding:          '6px 10px',
                              borderRadius:     5,
                              border:           '1px solid #334155',
                              backgroundColor:  'transparent',
                              color:            '#94a3b8',
                              fontSize:         9,
                              letterSpacing:    '0.12em',
                              cursor:           'pointer',
                              fontFamily:       'monospace',
                            }}>
                            CANCEL
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              </div>{/* end I-065 settled-greyout wrapper */}
              {msg && (
                <p style={{
                  marginTop:     10,
                  fontSize:      msg.ok ? 14 : 11,
                  lineHeight:    1.55,
                  color:         msg.ok ? 'rgba(187,247,208,0.92)' : 'rgba(239,68,68,0.8)',
                  fontFamily:    msg.ok ? "'Cormorant Garamond', 'Georgia', 'Times New Roman', serif" : 'monospace',
                  fontStyle:     msg.ok ? 'italic' : 'normal',
                  letterSpacing: msg.ok ? '0.01em' : 'normal',
                }}>
                  {msg.text}
                </p>
              )}
            </div>

            {/* Social Pulse — confidence-scored Newswire. Capped at
                40vh with a custom scrollbar; Simulated Alpha sits in
                the cockpit Footer row rather than above the Pulse. */}
            <div className="terminal-scroll">
              {!IS_STRIPPED_DOWN && <SocialPulseTicker lens={lens} />}
            </div>

            {/* Leaderboard — same height cap as Social Pulse. */}
            <div className="terminal-scroll">
              {!IS_STRIPPED_DOWN && <Leaderboard currentUser={currentUser} onOpenManifesto={openManifesto} />}
            </div>

            {/* Audit Log — hidden unless ?audit=1 or unlock flag is set */}
            <AuditLog userId={currentUser} />

            </div>{/* end .agora-drawer-body */}
          </div>{/* end .agora-drawer */}

          {/* Mobile-only scrim. Tap to dismiss. */}
          <div
            className={`agora-drawer-scrim ${drawerOpen ? 'open' : ''}`}
            onClick={() => setDrawerOpen(false)}
            aria-hidden={!drawerOpen}
          />
        </div>
      )}

      {/* ── Binary Maniswap markets (I-074 P3b) ─────────────────────────
            Arena-only YES/NO surface, fed from the binary engine on the
            Arena Supabase (separate from the Flask parimutuel feed above).
            Self-fetches + renders nothing when there are no OPEN binary
            markets, so it is invisible until an operator mints one. */}
      {IS_GAME_MODE && <BinaryMarketsPanel userId={currentUser} />}
      </>)}

      {/* ── NON-LOCAL lens preview ─────────────────────────────────────────
            §5 Multi-Market Lens Grid. When the user flips to Global, Asia
            or Europe we swap the entire live ward surface for a two-column
            preview: the mock-market grid on the left, Social Pulse ticker
            + Leaderboard on the right. The drawer/composer is hidden —
            those surfaces are coupled to the live AMM feed and don't
            apply to a preview. */}
      {view === 'markets' && lens !== 'LOCAL' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, height: '100%', minHeight: 0 }}>
          <div style={{ flex: '2 1 420px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, overflowY: 'auto' }}>
            {/* MintQueue is the Citizen-minted-markets rail — held back
                per DECISIONS.md 2026-05-13 I-046. Architect-minted seed
                markets continue to surface via PledgeCardGrid. */}
            {!IS_STRIPPED_DOWN && <MintQueue lens={lens} />}
            <PledgeCardGrid lens={lens} />
          </div>
          <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 0, minHeight: 0 }}>
            <div className="terminal-scroll">
              {!IS_STRIPPED_DOWN && <SocialPulseTicker lens={lens} />}
            </div>
            <div className="terminal-scroll">
              {!IS_STRIPPED_DOWN && <Leaderboard currentUser={currentUser} onOpenManifesto={openManifesto} />}
            </div>
          </div>
        </div>
      )}
      {/* end markets view */}

      </main>

      {/* ─ COCKPIT · ROW 3: Footer ─ The Simulated Alpha dashboard sits
            below the main surface. A thin metadata strip (version tag)
            sits inside the same row, right-aligned, so the cockpit is
            sealed top-to-bottom without reintroducing page scroll. */}
      <footer className="cockpit-footer" style={{
        borderTop:     '1px solid #1e293b',
        padding:       '6px 16px 8px',
        display:       'flex',
        flexDirection: 'column',
        gap:           4,
        position:      'relative',
        zIndex:        2,
        minHeight:     0,
        maxHeight:     '28vh',
        overflowY:     'auto',
        overflowX:     'hidden',
      }}>
        {/* DivergenceAlphaBox renders Against-the-Grain Rate,
            Reputation Alpha sparkline, Prophecy Leaderboard — all
            held back per DECISIONS.md 2026-05-13 I-046. */}
        {!IS_STRIPPED_DOWN && <DivergenceAlphaBox />}
        <div style={{ textAlign: 'right' }}>
          <span
            title={`Build tag · ${BUILD_TAG}`}
            style={{ fontFamily: 'monospace', fontSize: 8, color: '#475569', letterSpacing: '0.16em' }}
          >
            {APP_VERSION} · {BUILD_STAMP} · {BUILD_TAG} · ARCHITECT: {ARCHITECT}
          </span>
        </div>
      </footer>

      {/* Bug Report trigger lives in the top brand banner as a pill —
          see the Contact / Terms / Bug Report cluster above. The fixed
          bottom-right button was retired because the bottom Hazard
          Stripe partially obscured it. */}
      {feedbackOpen && (
        <FeedbackModal currentUser={currentUser} onClose={() => setFeedbackOpen(false)} />
      )}

      {/* ── Verification Required overlay ───────────────────────────────
            Raised when an anonymous wallet holder attempts to pledge on
            the Agoracle Global route. Rather than swallow the action
            with an inline error, we block with a gold-framed modal that
            names the required tier and points at the Citizen Seal
            claim flow. ──────────────────────────────────────────────── */}
      {verifyOverlay && (
        <div
          onClick={() => setVerifyOverlay(false)}
          style={{
            position:        'fixed',
            inset:           0,
            zIndex:          900,
            backgroundColor: 'rgba(2,6,23,0.88)',
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            padding:         '20px',
            backdropFilter:  'blur(4px)',
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: '#0f172a',
              border:          '1px solid #78350f',
              borderTop:       '2px solid #fcd34d',
              borderRadius:    10,
              padding:         '22px 24px',
              maxWidth:        440,
              width:           '100%',
              fontFamily:      'monospace',
              boxShadow:       '0 0 32px rgba(252,211,77,0.22)',
              position:        'relative',
            }}>
            {/* v1.8.1 Exit Valve — prominent top-right close. Backdrop
                click + DISMISS button already covered; this is the
                third escape path mandated by the Mint Isolation brief. */}
            <button
              onClick={() => setVerifyOverlay(false)}
              aria-label="Close verification modal"
              title="Close (Esc)"
              style={{
                position:        'absolute',
                top:             10,
                right:           10,
                background:      '#1e293b',
                border:          '1px solid #475569',
                color:           '#fcd34d',
                fontSize:        14,
                fontWeight:      700,
                cursor:          'pointer',
                lineHeight:      1,
                padding:         '3px 9px',
                borderRadius:    4,
                fontFamily:      'monospace',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#78350f'; e.currentTarget.style.borderColor = '#fcd34d'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#1e293b'; e.currentTarget.style.borderColor = '#475569'; }}
            >
              ✕
            </button>
            <p style={{ color: '#fcd34d', fontSize: 10, letterSpacing: '0.22em', margin: '0 0 10px', fontWeight: 700 }}>
              ⚱️ VERIFICATION REQUIRED
            </p>
            <p style={{ color: '#e2e8f0', fontSize: 13, lineHeight: 1.6, margin: '0 0 10px' }}>
              Agoracle Global pledges require <span style={{ color: '#f59e0b', fontWeight: 700 }}>Citizen</span>{' '}
              status (Level 1) or higher.
            </p>
            <p style={{ color: '#94a3b8', fontSize: 11, lineHeight: 1.6, margin: '0 0 18px' }}>
              You are currently anonymous. Claim your Citizen Seal — a non-transferrable
              proof-of-human badge — to unlock on-chain pledges and full P2P limits. Ghost
              Mode on the main Hub remains free and unrestricted.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => { setVerifyOverlay(false); openManifesto(); }}
                style={{
                  flex:             '1 1 200px',
                  padding:          '9px 12px',
                  backgroundColor:  '#451a03',
                  border:           '1px solid #92400e',
                  borderRadius:     6,
                  color:            '#fcd34d',
                  fontFamily:       'monospace',
                  fontSize:         10,
                  letterSpacing:    '0.14em',
                  cursor:           'pointer',
                }}>
                APPLY FOR CITIZEN SEAL
              </button>
              <button
                onClick={() => setVerifyOverlay(false)}
                style={{
                  padding:          '9px 14px',
                  backgroundColor:  'transparent',
                  border:           '1px solid #334155',
                  borderRadius:     6,
                  color:            '#94a3b8',
                  fontFamily:       'monospace',
                  fontSize:         10,
                  letterSpacing:    '0.14em',
                  cursor:           'pointer',
                }}>
                DISMISS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
